import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { Project } from '../../types';
import ProjectModal from '../Modals/ProjectModal';
import MLDSProjectModal from '../Modals/MLDSProjectModal';
import SubscriptionRequiredModal from '../Modals/SubscriptionRequiredModal';
import ProjectCard from '../Projects/ProjectCard';
import CloseProjectBilanModal, { BilanData, buildMldsBilanPayload } from '../Modals/CloseProjectBilanModal';
import ConfirmModal from '../Modals/ConfirmModal';
import CloseProjectBirthOverlay from '../Modals/CloseProjectBirthOverlay';
import CloseProjectModal from '../Modals/CloseProjectModal';
import './Projects.css';

// Imports API (Ajustez les chemins si nécessaire, basés sur la structure de Members.tsx)
import { getCurrentUser } from '../../api/Authentication';
import { deleteProject, getAllProjects, getAllUserProjects, getUserOrganizationProjects } from '../../api/Project';
import { getCompanyProjects, getSchoolProjects, getTeacherClasses } from '../../api/Dashboard';
import { closeProject, getTeacherProjects, postMldsBilan } from '../../api/Projects';
import {
  getOrganizationType,
  mapApiProjectToFrontendProject,
  projectBelongsToOrganizationContext,
} from '../../utils/projectMapper';
import { getSelectedOrganizationId as getSelectedOrgId, getFinancedProjectsCount, jeFinanceLabel } from '../../utils/contextUtils';
import FundedProjectsPage from './FundedProjectsPage';
import { openProjectAffiche, openProjectSpace } from '../../utils/projectSpaceStore';
import { canUserManageProject, canUserDeleteProject, isUserProjectOwner, isUserProjectCoOwner } from '../../utils/projectPermissions';
import { useToast } from '../../hooks/useToast';
import { isUnder15 } from '../../utils/ageUtils';
import { getSchoolLevels } from '../../api/SchoolDashboard/Levels';
import {
  countMldsByType,
  filterRawMldsProjects,
  isRawMldsProject,
  parseMldsClassLevelId,
  type MldsFilterParams,
  type MldsTypeFilter,
} from '../../utils/mldsProjectFilters';
import {
  dedupeProjectsById,
  fetchAllCompanyProjects,
  fetchAllLevelMldsProjects,
  fetchAllSchoolProjects,
  fetchAllTeacherProjectsPages,
  fetchAllUserProjectsPages,
} from '../../utils/mldsProjectFetch';
import { buildMyOrgProjectsParams } from '../../utils/orgProjectsApiParams';

type ProjectsHubTab =
  | 'nouveautes'
  | 'mes-projets'
  | 'mlds-projects'
  | 'mlds-remediation-projects'
  | 'brouillons'
  | 'archives'
  | 'coming'
  | 'in_progress'
  | 'ended'
  | 'je-finance';

const Projects: React.FC = () => {
  const { state, updateProject, setCurrentPage, setSelectedProject } = useAppContext();
  const { showError } = useToast();
  const navigate = useNavigate();
  const { selectedProject } = state;
  const financedCount = getFinancedProjectsCount(state.user, state.showingPageType);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isMLDSProjectModalOpen, setIsMLDSProjectModalOpen] = useState(false);
  const [mldsProjectVariant, setMldsProjectVariant] = useState<'perseverance' | 'remediation'>('perseverance');
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [isCloseProjectConfirmOpen, setIsCloseProjectConfirmOpen] = useState(false);
  const [isCloseProjectModalOpen, setIsCloseProjectModalOpen] = useState(false);
  const [projectToClose, setProjectToClose] = useState<Project | null>(null);
  const [isClosingProject, setIsClosingProject] = useState(false);
  const [bornProofProject, setBornProofProject] = useState<Project | null>(null);
  const [duplicateSourceProject, setDuplicateSourceProject] = useState<Project | null>(null);
  
  // State local pour stocker les projets récupérés de l'API
  const [projects, setProjects] = useState<Project[]>([]);
  const [myProjects, setMyProjects] = useState<Project[]>([]);
  const [mldsProjects, setMldsProjects] = useState<Project[]>([]);
  const [mldsRemediationProjects, setMldsRemediationProjects] = useState<Project[]>([]);
  // Store raw API project data for permission checks (projectId -> raw API project)
  const [rawProjectsMap, setRawProjectsMap] = useState<Map<string, any>>(new Map());
  // Store all filtered projects for client-side pagination (for filters that need client-side filtering)
  const [allFilteredProjects, setAllFilteredProjects] = useState<any[]>([]);
  // Skip fetch in filter effect on initial mount (avoid duplicate with projectPage effect)
  const filtersEffectInitialMount = React.useRef(true);
  // Track if initial fetchProjects has been called to avoid duplicate in projectPage effect
  const initialProjectsFetched = React.useRef(false);
  // Cache for MLDS tabs: full dataset per org context (filter + paginate client-side)
  const mldsProjectsCacheRef = React.useRef<{ key: string; rawAll: any[] } | null>(null);
  const [mldsCatalogCounts, setMldsCatalogCounts] = useState({ perseverance: 0, remediation: 0 });

  // Pagination state for "Nouveautés" tab (public projects for user dashboard, org projects for pro/edu)
  const [projectPage, setProjectPage] = useState(1);
  const [projectTotalPages, setProjectTotalPages] = useState(1);
  const [projectTotalCount, setProjectTotalCount] = useState(0);
  
  // Pagination state for "Mes projets" tab (user's own projects)
  const [myProjectsPage, setMyProjectsPage] = useState(1);
  const [myProjectsTotalPages, setMyProjectsTotalPages] = useState(1);
  const [myProjectsTotalCount, setMyProjectsTotalCount] = useState(0);
  
  // Pagination state for "Projets MLDS" tab
  const [mldsProjectsPage, setMldsProjectsPage] = useState(1);
  const [mldsProjectsTotalPages, setMldsProjectsTotalPages] = useState(1);
  const [mldsProjectsTotalCount, setMldsProjectsTotalCount] = useState(0);
  // Pagination state for "Projets MLDS Remédiation" tab
  const [mldsRemediationProjectsPage, setMldsRemediationProjectsPage] = useState(1);
  const [mldsRemediationProjectsTotalPages, setMldsRemediationProjectsTotalPages] = useState(1);
  const [mldsRemediationProjectsTotalCount, setMldsRemediationProjectsTotalCount] = useState(0);
  
  // Tab state for all users
  // Teachers should only see their own projects, not public projects
  const isPersonalUser = state.showingPageType === 'teacher' || state.showingPageType === 'user';
  const isTeacher = state.showingPageType === 'teacher';
  const isMinorPersonalUser = state.showingPageType === 'user' && isUnder15(state.user?.birthday);
  const isOrgHub = state.showingPageType === 'pro' || state.showingPageType === 'edu';
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ProjectsHubTab>(() => {
    if (searchParams.get('tab') === 'je-finance') return 'je-finance';
    if (isTeacher) return 'mes-projets';
    if (isOrgHub) return 'in_progress';
    return 'nouveautes';
  });

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 250);

    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const [pathwayFilter, setPathwayFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(isOrgHub ? 'En cours' : 'all');
  const [financementFilter, setFinancementFilter] = useState<'all' | 'with' | 'without'>('all');
  const [lifecycleCounts, setLifecycleCounts] = useState({ coming: 0, in_progress: 0, ended: 0 });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [organizationFilter, setOrganizationFilter] = useState<'my-org' | 'all-public' | 'school' | 'other-orgs' | 'other-schools' | 'companies'>('my-org');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  
  // MLDS-specific filter states
  // Keep filters independent between Persévérance vs Remédiation tabs
  const [mldsPerseveranceRequestedByFilter, setMldsPerseveranceRequestedByFilter] = useState('all');
  const [mldsPerseveranceTargetAudienceFilter, setMldsPerseveranceTargetAudienceFilter] = useState('all');
  const [mldsPerseveranceActionObjectivesFilter, setMldsPerseveranceActionObjectivesFilter] = useState('all');
  const [mldsPerseveranceOrganizationFilter, setMldsPerseveranceOrganizationFilter] = useState('all');
  const [mldsPerseveranceStatusFilter, setMldsPerseveranceStatusFilter] = useState('all');
  const [mldsPerseveranceStartDate, setMldsPerseveranceStartDate] = useState('');
  const [mldsPerseveranceEndDate, setMldsPerseveranceEndDate] = useState('');

  const [mldsRemediationRequestedByFilter, setMldsRemediationRequestedByFilter] = useState('all');
  const [mldsRemediationTargetAudienceFilter, setMldsRemediationTargetAudienceFilter] = useState('all');
  const [mldsRemediationActionObjectivesFilter, setMldsRemediationActionObjectivesFilter] = useState('all');
  const [mldsRemediationOrganizationFilter, setMldsRemediationOrganizationFilter] = useState('all');
  const [mldsRemediationStatusFilter, setMldsRemediationStatusFilter] = useState('all');
  const [mldsRemediationStartDate, setMldsRemediationStartDate] = useState('');
  const [mldsRemediationEndDate, setMldsRemediationEndDate] = useState('');
  const [archivedTypeFilter, setArchivedTypeFilter] = useState<'all' | 'classic' | 'mlds'>('all');

  // Loading states (main list vs MLDS are isolated to avoid empty-state flash)
  const [isLoadingMainProjects, setIsLoadingMainProjects] = useState(true);
  const [isLoadingMldsProjects, setIsLoadingMldsProjects] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // Draft projects (teacher / pro / edu): loaded when "Brouillons" tab is active
  const [draftProjects, setDraftProjects] = useState<Project[]>([]);
  const [isLoadingDraftProjects, setIsLoadingDraftProjects] = useState(false);

  // Archived projects: loaded when "Archives" tab is active
  const [archivedProjects, setArchivedProjects] = useState<Project[]>([]);
  const [isLoadingArchivedProjects, setIsLoadingArchivedProjects] = useState(false);

  // MLDS filter options: list all classes (levels) in current context
  const [mldsFilterClasses, setMldsFilterClasses] = useState<Array<{ id: number; name?: string; level?: string | number }>>([]);

  // Fonction pour obtenir l'organizationId sélectionné (comme dans Dashboard)
  const getSelectedOrganizationId = (): number | undefined => {
    return getSelectedOrgId(state.user, state.showingPageType);
  };

  const isLifecycleTab = activeTab === 'coming' || activeTab === 'in_progress' || activeTab === 'ended';

  const writeHubTabToUrl = (tab: ProjectsHubTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'je-finance') next.set('tab', 'je-finance');
      else next.delete('tab');
      return next;
    }, { replace: true });
  };

  const selectHubTab = (tab: ProjectsHubTab) => {
    setActiveTab(tab);
    writeHubTabToUrl(tab);
  };

  const selectLifecycleTab = (tab: 'coming' | 'in_progress' | 'ended') => {
    selectHubTab(tab);
    setProjectPage(1);
    setStatusFilter(tab === 'coming' ? 'À venir' : tab === 'in_progress' ? 'En cours' : 'Terminée');
  };

  useEffect(() => {
    if (searchParams.get('tab') === 'je-finance') {
      setActiveTab((current) => (current === 'je-finance' ? current : 'je-finance'));
    }
  }, [searchParams]);

  const fetchLifecycleCounts = React.useCallback(async () => {
    if (!isOrgHub) return;
    const contextId = getSelectedOrgId(state.user, state.showingPageType);
    if (!contextId) return;
    const isEdu = state.showingPageType === 'edu';
    const fetchOne = async (filter: string) => {
      const params = buildMyOrgProjectsParams(1, filter);
      params.per_page = 1;
      const response = isEdu
        ? await getSchoolProjects(contextId, params)
        : await getCompanyProjects(contextId, params);
      return response.data?.meta?.total_count || 0;
    };
    try {
      const [coming, inProgress, ended] = await Promise.all([
        fetchOne('À venir'),
        fetchOne('En cours'),
        fetchOne('Terminée'),
      ]);
      setLifecycleCounts({ coming, in_progress: inProgress, ended });
    } catch {
      /* keep previous counts */
    }
  }, [isOrgHub, state.user, state.showingPageType]);

  useEffect(() => {
    void fetchLifecycleCounts();
  }, [fetchLifecycleCounts]);

  // Fonction pour récupérer les projets publics (Nouveautés)
  const fetchPublicProjects = React.useCallback(async (page: number = 1) => {
    setIsLoadingMainProjects(true);
    try {
      const currentUser = await getCurrentUser();
      
      // Pour les utilisateurs personnels, toujours récupérer tous les projets publics
      // (le filtre d'organisation n'est pas applicable)
      const apiParams: { page?: number; per_page?: number; public_only?: boolean; search?: string } = {
        page: page,
        per_page: 12,
        public_only: true
      };
      if (debouncedSearchTerm) apiParams.search = debouncedSearchTerm;

      const response = await getAllProjects(apiParams);
      const rawProjects = response.data?.data || response.data || [];
      const formattedProjects: Project[] = rawProjects.map((p: any) => {
        return mapApiProjectToFrontendProject(p, state.showingPageType, currentUser.data);
      });
      // Badge counts are now included in API response via badge_count
      setProjects(formattedProjects);
      
      // Extract and store pagination metadata
      if (response.data?.meta) {
        setProjectTotalPages(response.data.meta.total_pages || 1);
        setProjectTotalCount(response.data.meta.total_count || 0);
      }
      setInitialLoad(false);
    } catch (err) {
      console.error('Erreur lors de la récupération des projets publics:', err);
      showError('Erreur lors de la récupération des projets publics.');
      setProjects([]);
      setProjectTotalPages(1);
      setProjectTotalCount(0);
      setInitialLoad(false);
    } finally {
      setIsLoadingMainProjects(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showingPageType, organizationFilter, debouncedSearchTerm]);

  // Projets des établissements/organisations dont l'utilisateur est membre confirmé (pour mineurs < 15)
  const fetchOrganizationProjects = React.useCallback(async (page: number = 1) => {
    setIsLoadingMainProjects(true);
    try {
      const currentUser = await getCurrentUser();
      const params: { page?: number; per_page?: number; search?: string } = { page, per_page: 12 };
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      const response = await getUserOrganizationProjects(params);
      const rawProjects = response.data?.data || response.data || [];
      const formattedProjects: Project[] = rawProjects.map((p: any) => {
        return mapApiProjectToFrontendProject(p, state.showingPageType, currentUser.data);
      });
      setProjects(formattedProjects);
      const newRawProjectsMap = new Map<string, any>();
      rawProjects.forEach((p: any) => {
        if (p.id) newRawProjectsMap.set(p.id.toString(), p);
      });
      setRawProjectsMap(prev => {
        const merged = new Map(prev);
        newRawProjectsMap.forEach((value, key) => merged.set(key, value));
        return merged;
      });
      if (response.data?.meta) {
        setProjectTotalPages(response.data.meta.total_pages || 1);
        setProjectTotalCount(response.data.meta.total_count || 0);
      }
      setInitialLoad(false);
    } catch (err) {
      console.error('Erreur lors de la récupération des projets des établissements/organisations:', err);
      setProjects([]);
      setProjectTotalPages(1);
      setProjectTotalCount(0);
      setInitialLoad(false);
    } finally {
      setIsLoadingMainProjects(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showingPageType, debouncedSearchTerm]);

  // Fonction pour récupérer les projets de l'utilisateur (Mes projets)
  const fetchMyProjects = React.useCallback(async (page: number = 1) => {
    setIsLoadingMainProjects(true);
    try {
      const currentUser = await getCurrentUser();
      
      if (state.showingPageType === 'teacher') {
        // Pour les enseignants : utiliser getTeacherProjects
        const response = await getTeacherProjects({ page: page, per_page: 12 });
        const rawProjects = response.data || [];
        
        // Exclure les projets MLDS
        const nonMLDSProjects = rawProjects.filter((p: any) => p.mlds_information == null);
        
        // Store raw API projects for permission checks
        const newRawProjectsMap = new Map<string, any>();
        nonMLDSProjects.forEach((p: any) => {
          if (p.id) {
            newRawProjectsMap.set(p.id.toString(), p);
          }
        });
        setRawProjectsMap(prev => {
          const merged = new Map(prev);
          newRawProjectsMap.forEach((value, key) => merged.set(key, value));
          return merged;
        });
        
        const formattedProjects: Project[] = nonMLDSProjects.map((p: any) => {
          return mapApiProjectToFrontendProject(p, state.showingPageType, currentUser.data);
        });
        // Badge counts are now included in API response via badge_count
        setMyProjects(formattedProjects);
        
        // Extract and store pagination metadata
        if (response.meta) {
          setMyProjectsTotalPages(response.meta.total_pages || 1);
          setMyProjectsTotalCount(response.meta.total_count || 0);
        }
      } else if (state.showingPageType === 'user') {
        // Pour les utilisateurs : utiliser getAllUserProjects
        const params: { page?: number; per_page?: number; search?: string } = { page: page, per_page: 12 };
        if (debouncedSearchTerm) params.search = debouncedSearchTerm;
        const response = await getAllUserProjects(params);
        const rawProjects = response.data?.data || response.data || [];
        
        // Exclure les projets MLDS
        const nonMLDSProjects = rawProjects.filter((p: any) => p.mlds_information == null);
        
        // Store raw API projects for permission checks
        const newRawProjectsMap = new Map<string, any>();
        nonMLDSProjects.forEach((p: any) => {
          if (p.id) {
            newRawProjectsMap.set(p.id.toString(), p);
          }
        });
        setRawProjectsMap(prev => {
          const merged = new Map(prev);
          newRawProjectsMap.forEach((value, key) => merged.set(key, value));
          return merged;
        });
        
        const formattedProjects: Project[] = nonMLDSProjects.map((p: any) => {
          return mapApiProjectToFrontendProject(p, state.showingPageType, currentUser.data);
        });
        // Badge counts are now included in API response via badge_count
        setMyProjects(formattedProjects);
        
        // Extract and store pagination metadata
        if (response.data?.meta) {
          setMyProjectsTotalPages(response.data.meta.total_pages || 1);
          setMyProjectsTotalCount(response.data.meta.total_count || 0);
        }
      }
      setInitialLoad(false);
    } catch (err) {
      console.error('Erreur lors de la récupération de mes projets:', err);
      showError('Erreur lors de la récupération de mes projets.');
      setMyProjects([]);
      setMyProjectsTotalPages(1);
      setMyProjectsTotalCount(0);
      setInitialLoad(false);
    } finally {
      setIsLoadingMainProjects(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showingPageType, debouncedSearchTerm]);

  const buildMldsCacheKey = React.useCallback(() => {
    const contextId = getSelectedOrganizationId() ?? '';
    return `${state.showingPageType}:${contextId}`;
  }, [state.showingPageType, state.user]);

  const buildMldsFilterParams = React.useCallback(
    (typeFilter: MldsTypeFilter): MldsFilterParams => ({
      typeFilter,
      searchTerm: debouncedSearchTerm,
      requestedBy:
        typeFilter === 'remediation'
          ? mldsRemediationRequestedByFilter
          : mldsPerseveranceRequestedByFilter,
      targetAudience:
        typeFilter === 'remediation'
          ? mldsRemediationTargetAudienceFilter
          : mldsPerseveranceTargetAudienceFilter,
      actionObjectives:
        typeFilter === 'remediation'
          ? mldsRemediationActionObjectivesFilter
          : mldsPerseveranceActionObjectivesFilter,
      organizationFilter:
        typeFilter === 'remediation'
          ? mldsRemediationOrganizationFilter
          : mldsPerseveranceOrganizationFilter,
      statusFilter:
        typeFilter === 'remediation' ? mldsRemediationStatusFilter : mldsPerseveranceStatusFilter,
      startDate: typeFilter === 'remediation' ? mldsRemediationStartDate : mldsPerseveranceStartDate,
      endDate: typeFilter === 'remediation' ? mldsRemediationEndDate : mldsPerseveranceEndDate,
    }),
    [
      debouncedSearchTerm,
      mldsPerseveranceRequestedByFilter,
      mldsPerseveranceTargetAudienceFilter,
      mldsPerseveranceActionObjectivesFilter,
      mldsPerseveranceOrganizationFilter,
      mldsPerseveranceStatusFilter,
      mldsPerseveranceStartDate,
      mldsPerseveranceEndDate,
      mldsRemediationRequestedByFilter,
      mldsRemediationTargetAudienceFilter,
      mldsRemediationActionObjectivesFilter,
      mldsRemediationOrganizationFilter,
      mldsRemediationStatusFilter,
      mldsRemediationStartDate,
      mldsRemediationEndDate,
    ]
  );

  const invalidateMldsCache = React.useCallback(() => {
    mldsProjectsCacheRef.current = null;
  }, []);

  const loadMldsProjectsRawAll = React.useCallback(
    async (force = false) => {
      const cacheKey = buildMldsCacheKey();
      if (!force && mldsProjectsCacheRef.current?.key === cacheKey) {
        return;
      }

      setIsLoadingMldsProjects(true);
      try {
        let rawProjects: any[] = [];

        if (state.showingPageType === 'teacher') {
          rawProjects = await fetchAllTeacherProjectsPages();
        } else if (state.showingPageType === 'user') {
          rawProjects = await fetchAllUserProjectsPages();
        } else {
          const contextId = getSelectedOrganizationId();
          if (!contextId) {
            console.warn('⚠️ [Projects MLDS] Aucun contextId trouvé pour le type:', state.showingPageType);
            mldsProjectsCacheRef.current = { key: cacheKey, rawAll: [] };
            setMldsCatalogCounts({ perseverance: 0, remediation: 0 });
            return;
          }

          const isEdu = state.showingPageType === 'edu';
          rawProjects = isEdu
            ? await fetchAllSchoolProjects(contextId, true)
            : await fetchAllCompanyProjects(contextId, true);

          const contextType = isEdu ? 'school' : 'company';
          try {
            const myRaw = await fetchAllUserProjectsPages();
            const scopedMyProjects = myRaw.filter((p: any) =>
              projectBelongsToOrganizationContext(p, contextId, contextType)
            );
            rawProjects = [...rawProjects, ...scopedMyProjects];
          } catch {
            // Best-effort
          }
        }

        rawProjects = dedupeProjectsById(rawProjects);

        // Phase 2: merge class-scoped MLDS projects when a class filter is selected
        const classLevelIds = new Set<number>();
        const pClass = parseMldsClassLevelId(mldsPerseveranceOrganizationFilter);
        const rClass = parseMldsClassLevelId(mldsRemediationOrganizationFilter);
        if (pClass != null) classLevelIds.add(pClass);
        if (rClass != null) classLevelIds.add(rClass);

        for (const levelId of Array.from(classLevelIds)) {
          try {
            const classProjects = await fetchAllLevelMldsProjects(levelId);
            rawProjects = dedupeProjectsById([...rawProjects, ...classProjects]);
          } catch (e) {
            console.error(`Error fetching MLDS projects for class ${levelId}:`, e);
          }
        }

        mldsProjectsCacheRef.current = { key: cacheKey, rawAll: rawProjects };
        setMldsCatalogCounts(countMldsByType(rawProjects.filter(isRawMldsProject)));
      } catch (err) {
        console.error('Erreur lors de la récupération des projets MLDS:', err);
        showError('Erreur lors de la récupération des projets MLDS.');
        mldsProjectsCacheRef.current = { key: cacheKey, rawAll: [] };
        setMldsCatalogCounts({ perseverance: 0, remediation: 0 });
      } finally {
        setIsLoadingMldsProjects(false);
      }
    },
    [
      buildMldsCacheKey,
      state.showingPageType,
      state.user,
      mldsPerseveranceOrganizationFilter,
      mldsRemediationOrganizationFilter,
    ]
  );

  const applyMldsDisplay = React.useCallback(
    (page: number, typeFilter: MldsTypeFilter) => {
      const rawAll = mldsProjectsCacheRef.current?.rawAll ?? [];
      const filterParams = buildMldsFilterParams(typeFilter);
      const filtered = filterRawMldsProjects(rawAll, filterParams);

      const newRawProjectsMap = new Map<string, any>();
      filtered.forEach((p: any) => {
        if (p.id) newRawProjectsMap.set(p.id.toString(), p);
      });
      setRawProjectsMap((prev) => {
        const merged = new Map(prev);
        newRawProjectsMap.forEach((value, key) => merged.set(key, value));
        return merged;
      });

      const perPage = 12;
      const totalCount = filtered.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
      const startIndex = (page - 1) * perPage;
      const pageSlice = filtered.slice(startIndex, startIndex + perPage);

      const formattedProjects: Project[] = pageSlice.map((p: any) =>
        mapApiProjectToFrontendProject(p, state.showingPageType, state.user)
      );

      if (typeFilter === 'remediation') {
        setMldsRemediationProjects(formattedProjects);
        setMldsRemediationProjectsTotalPages(totalPages);
        setMldsRemediationProjectsTotalCount(totalCount);
      } else {
        setMldsProjects(formattedProjects);
        setMldsProjectsTotalPages(totalPages);
        setMldsProjectsTotalCount(totalCount);
      }
    },
    [buildMldsFilterParams, state.showingPageType, state.user]
  );

  const refreshMldsProjects = React.useCallback(
    async (force = false) => {
      await loadMldsProjectsRawAll(force);
      applyMldsDisplay(mldsProjectsPage, 'perseverance');
      applyMldsDisplay(mldsRemediationProjectsPage, 'remediation');
    },
    [loadMldsProjectsRawAll, applyMldsDisplay, mldsProjectsPage, mldsRemediationProjectsPage]
  );

  const fetchMLDSProjects = React.useCallback(
    async (page: number = 1, typeFilter: MldsTypeFilter = 'perseverance') => {
      if (!mldsProjectsCacheRef.current) {
        await loadMldsProjectsRawAll(false);
      }
      applyMldsDisplay(page, typeFilter);
    },
    [loadMldsProjectsRawAll, applyMldsDisplay]
  );

  // Fetch draft projects for "Brouillons" tab (teacher and pro/edu)
  const fetchDraftProjects = React.useCallback(async () => {
    setIsLoadingDraftProjects(true);
    try {
      const currentUser = await getCurrentUser();
      let rawProjects: any[] = [];

      if (state.showingPageType === 'teacher') {
        const response = await getTeacherProjects({ status: 'draft', per_page: 100 });
        rawProjects = (response.data || []).filter(
          (p: any) => p.mlds_information == null && p.status === 'draft'
        );
      } else if (state.showingPageType === 'edu' || state.showingPageType === 'pro') {
        const contextId = getSelectedOrganizationId();
        if (!contextId) {
          setDraftProjects([]);
          return;
        }
        const isEdu = state.showingPageType === 'edu';
        const response = isEdu
          ? await getSchoolProjects(contextId, true, 200, 1)
          : await getCompanyProjects(contextId, true, 200, 1);
        rawProjects = (response.data?.data || response.data || []).filter(
          (p: any) => p.mlds_information == null && p.status === 'draft'
        );
      } else {
        setDraftProjects([]);
        return;
      }

      const formatted: Project[] = rawProjects.map((p: any) =>
        mapApiProjectToFrontendProject(p, state.showingPageType, currentUser.data)
      );
      setDraftProjects(formatted);

      const newRawMap = new Map<string, any>();
      rawProjects.forEach((p: any) => {
        if (p.id) newRawMap.set(p.id.toString(), p);
      });
      setRawProjectsMap(prev => {
        const merged = new Map(prev);
        newRawMap.forEach((v, k) => merged.set(k, v));
        return merged;
      });
    } catch (err) {
      console.error('Erreur lors de la récupération des brouillons:', err);
      showError('Erreur lors de la récupération des brouillons.');
      setDraftProjects([]);
    } finally {
      setIsLoadingDraftProjects(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showingPageType]);

  // Fetch archived projects for "Archives" tab
  const fetchArchivedProjects = React.useCallback(async () => {
    setIsLoadingArchivedProjects(true);
    try {
      const currentUser = await getCurrentUser();
      let rawProjects: any[] = [];

      if (state.showingPageType === 'teacher') {
        const response = await getTeacherProjects({ status: 'archived', per_page: 200 });
        rawProjects = (response.data || []).filter((p: any) => p.status === 'archived');
      } else if (state.showingPageType === 'edu' || state.showingPageType === 'pro') {
        const contextId = getSelectedOrganizationId();
        if (!contextId) {
          setArchivedProjects([]);
          return;
        }
        const isEdu = state.showingPageType === 'edu';
        const response = isEdu
          ? await getSchoolProjects(contextId, true, 200, 1)
          : await getCompanyProjects(contextId, true, 200, 1);
        rawProjects = (response.data?.data || response.data || []).filter(
          (p: any) => p.status === 'archived'
        );
      } else if (state.showingPageType === 'user') {
        const response = await getAllUserProjects({ per_page: 200 });
        rawProjects = (response.data?.data || response.data || []).filter(
          (p: any) => p.status === 'archived'
        );
      } else {
        setArchivedProjects([]);
        return;
      }

      const formatted: Project[] = rawProjects.map((p: any) =>
        mapApiProjectToFrontendProject(p, state.showingPageType, currentUser.data)
      );
      setArchivedProjects(formatted);

      const newRawMap = new Map<string, any>();
      rawProjects.forEach((p: any) => {
        if (p.id) newRawMap.set(p.id.toString(), p);
      });
      setRawProjectsMap(prev => {
        const merged = new Map(prev);
        newRawMap.forEach((v, k) => merged.set(k, v));
        return merged;
      });
    } catch (err) {
      console.error('Erreur lors de la récupération des archives:', err);
      showError('Erreur lors de la récupération des archives.');
      setArchivedProjects([]);
    } finally {
      setIsLoadingArchivedProjects(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showingPageType]);

  // Fonction pour récupérer les projets (réutilisable)
  const fetchProjects = React.useCallback(async (page: number = 1) => {
    setIsLoadingMainProjects(true);
    try {
      const currentUser = state.user ?? (await getCurrentUser()).data;

      if (isTeacher) {
        // Pour les enseignants : charger uniquement leurs projets (créés + participants)
        // Ne pas charger les projets publics
        await fetchMyProjects(page);
      } else if (state.showingPageType === 'user') {
        // Pour les utilisateurs personnels : mineurs < 15 → projets des établissements/orgs, sinon projets publics (Nouveautés)
        if (isMinorPersonalUser) {
          await fetchOrganizationProjects(page);
        } else {
          await fetchPublicProjects(page);
        }
        // Charger aussi les projets de l'utilisateur (Mes projets)
        await fetchMyProjects(myProjectsPage);
      } else {
        // Pour les rôles pro et edu, gérer les différents filtres d'organisation
        let response: any;
        let rawProjects: any[] = [];
        let needsClientSideFiltering = false;
        
        const isEdu = state.showingPageType === 'edu';
        const contextId = getSelectedOrganizationId();

        if (organizationFilter === 'my-org') {
          // Mon organisation / Mon établissement: server-side filters + pagination
          if (!contextId) {
            console.warn('⚠️ [Projects] Aucun contextId trouvé pour le type:', state.showingPageType);
            setProjects([]);
            setProjectTotalPages(1);
            setProjectTotalCount(0);
            return;
          }

          const apiParams = buildMyOrgProjectsParams(page, statusFilter, debouncedSearchTerm);
          if (isEdu) {
            response = await getSchoolProjects(contextId, apiParams);
          } else {
            response = await getCompanyProjects(contextId, apiParams);
          }
          rawProjects = response.data?.data || response.data || [];
        } else if (organizationFilter === 'all-public') {
          // Tous les projets (publics uniquement, hors MLDS) — filtre serveur + pagination cohérente
          response = await getAllProjects({ page: page, per_page: 12, public_only: true, search: debouncedSearchTerm || undefined });
          rawProjects = response.data?.data || response.data || [];
          needsClientSideFiltering = false;
        } else if (organizationFilter === 'school') {
          // Établissement: utiliser getAllProjects avec organization_type: 'établissement' (Pro only)
          if (state.showingPageType !== 'pro') {
            console.warn('⚠️ [Projects] "school" filter should only be used on Pro dashboard');
            setProjects([]);
            setProjectTotalPages(1);
            setProjectTotalCount(0);
            return;
          }
          response = await getAllProjects({ organization_type: 'établissement', page: page, per_page: 12, search: debouncedSearchTerm || undefined });
          rawProjects = response.data?.data || response.data || [];
          // Exclure les projets MLDS
          rawProjects = rawProjects.filter((p: any) => p.mlds_information == null);
        } else if (organizationFilter === 'other-orgs') {
          // Autres organisations: utiliser getAllProjects sans filtre, puis filtrer côté client (Pro only)
          if (state.showingPageType !== 'pro') {
            console.warn('⚠️ [Projects] "other-orgs" filter should only be used on Pro dashboard');
            setProjects([]);
            setProjectTotalPages(1);
            setProjectTotalCount(0);
            return;
          }
          // For client-side filtering, fetch a large batch to get all projects, then filter and paginate client-side
          response = await getAllProjects({ page: 1, per_page: 200, search: debouncedSearchTerm || undefined });
          rawProjects = response.data?.data || response.data || [];
          needsClientSideFiltering = true;
        } else if (organizationFilter === 'other-schools') {
          // Autres établissements: utiliser getAllProjects avec organization_type: 'établissement', puis filtrer côté client (Edu only)
          if (state.showingPageType !== 'edu') {
            console.warn('⚠️ [Projects] "other-schools" filter should only be used on Edu dashboard');
            setProjects([]);
            setProjectTotalPages(1);
            setProjectTotalCount(0);
            return;
          }
          // For client-side filtering, fetch a large batch to get all projects, then filter and paginate client-side
          response = await getAllProjects({ organization_type: 'établissement', page: 1, per_page: 200, search: debouncedSearchTerm || undefined });
          rawProjects = response.data?.data || response.data || [];
          needsClientSideFiltering = true;
        } else if (organizationFilter === 'companies') {
          // Organisations: utiliser getAllProjects sans filtre, puis filtrer côté client (Edu only)
          if (state.showingPageType !== 'edu') {
            console.warn('⚠️ [Projects] "companies" filter should only be used on Edu dashboard');
            setProjects([]);
            setProjectTotalPages(1);
            setProjectTotalCount(0);
            return;
          }
          // For client-side filtering, fetch a large batch to get all projects, then filter and paginate client-side
          response = await getAllProjects({ page: 1, per_page: 200, search: debouncedSearchTerm || undefined });
          rawProjects = response.data?.data || response.data || [];
          needsClientSideFiltering = true;
        } else {
          // Fallback: should not happen, but TypeScript needs this
          console.warn('⚠️ [Projects] Unknown organization filter:', organizationFilter);
          setProjects([]);
          setProjectTotalPages(1);
          setProjectTotalCount(0);
          return;
        }

        // Client-side filtering for 'other-orgs', 'other-schools', and 'companies' ('all-public' uses public_only on API)
        if (needsClientSideFiltering) {
          let filteredProjects: any[] = [];

          if (organizationFilter === 'other-orgs') {
            // Pro: Company projects excluding user's own
            filteredProjects = rawProjects.filter((p: any) => {
              if (p.private) return false;
              // Exclude MLDS projects
              if (p.mlds_information != null) return false;
              // Must have company_ids (projects without company_ids are likely school projects)
              if (!p.company_ids || !Array.isArray(p.company_ids) || p.company_ids.length === 0) {
                return false;
              }
              // Exclude school projects (check if has school_levels or school_level_ids, even if empty arrays)
              // Also exclude if owner's role suggests it's a school project
              if ((p.school_levels && Array.isArray(p.school_levels) && p.school_levels.length > 0) ||
                  (p.school_level_ids && Array.isArray(p.school_level_ids) && p.school_level_ids.length > 0)) {
                return false;
              }
              // Additional check: exclude if owner role indicates school project
              if (p.owner?.role) {
                const schoolRoles = ['directeur_ecole', 'primary_school_teacher', 'other_school_admin', 
                                     'collegien', 'lyceen', 'eleve_primaire', 'enseignant'];
                if (schoolRoles.includes(p.owner.role)) {
                  return false;
                }
              }
              // Exclude user's own company
              if (contextId && p.company_ids.includes(contextId)) {
                return false;
              }
              return true;
            });
            // Store all filtered projects and paginate client-side
            setAllFilteredProjects(filteredProjects);
            const perPage = 12;
            const startIndex = (page - 1) * perPage;
            const endIndex = startIndex + perPage;
            rawProjects = filteredProjects.slice(startIndex, endIndex);
          } else if (organizationFilter === 'other-schools') {
            // Edu: Other school projects excluding user's own
            // Note: API call with organization_type: 'établissement' returns all school projects
            // So we just need to filter out user's own school and private projects
            filteredProjects = rawProjects.filter((p: any) => {
              if (p.private) return false;
              // Exclude MLDS projects
              if (p.mlds_information != null) return false;
              
              // Get all user's school IDs
              const userSchoolIds = state.user?.available_contexts?.schools?.map((s: any) => s.id) || [];
              const currentUserId = state.user?.id?.toString();
              
              // Exclude projects owned by the current user
              if (currentUserId && p.owner?.id?.toString() === currentUserId) {
                return false;
              }
              
              // Exclude projects linked to user's schools via school_levels
              if (userSchoolIds.length > 0) {
                if (p.school_levels && Array.isArray(p.school_levels) && p.school_levels.length > 0) {
                  const hasUserSchool = p.school_levels.some((sl: any) => 
                    sl.school_id && userSchoolIds.includes(sl.school_id)
                  );
                  if (hasUserSchool) return false;
                }
                if (p.school_level_ids && Array.isArray(p.school_level_ids) && p.school_level_ids.length > 0) {
                  // Note: school_level_ids are level IDs, not school IDs, so we need to check differently
                  // For now, if a project has school_level_ids, we'll check via school_levels array
                }
              }
              
              return true;
            });
            // Store all filtered projects and paginate client-side
            setAllFilteredProjects(filteredProjects);
            const perPage = 12;
            const startIndex = (page - 1) * perPage;
            const endIndex = startIndex + perPage;
            rawProjects = filteredProjects.slice(startIndex, endIndex);
          } else if (organizationFilter === 'companies') {
            // Edu: All company projects
            filteredProjects = rawProjects.filter((p: any) => {
              if (p.private) return false;
              // Exclude MLDS projects
              if (p.mlds_information != null) return false;
              // Must have company_ids
              if (!p.company_ids || !Array.isArray(p.company_ids) || p.company_ids.length === 0) {
                return false;
              }
              // Exclude school projects (projects with school_levels)
              if ((p.school_levels && Array.isArray(p.school_levels) && p.school_levels.length > 0) ||
                  (p.school_level_ids && Array.isArray(p.school_level_ids) && p.school_level_ids.length > 0)) {
                return false;
              }
              return true;
            });
            // Store all filtered projects and paginate client-side
            setAllFilteredProjects(filteredProjects);
            const perPage = 12;
            const startIndex = (page - 1) * perPage;
            const endIndex = startIndex + perPage;
            rawProjects = filteredProjects.slice(startIndex, endIndex);
          }
        }

        // Mapping des données API vers le type Project (using centralized mapper)
        const formattedProjects: Project[] = rawProjects.map((p: any) => {
            return mapApiProjectToFrontendProject(p, state.showingPageType, currentUser);
        });

        // Store raw API projects for permission checks
        const newRawProjectsMap = new Map<string, any>();
        rawProjects.forEach((p: any) => {
          if (p.id) {
            newRawProjectsMap.set(p.id.toString(), p);
          }
        });
        setRawProjectsMap(prev => {
          const merged = new Map(prev);
          newRawProjectsMap.forEach((value, key) => merged.set(key, value));
          return merged;
        });

        // Badge counts are now included in API response via badge_count
        setProjects(formattedProjects);
        
        // Extract and store pagination metadata
        if (response && response.data?.meta) {
          let totalPages = response.data.meta.total_pages || 1;
          let totalCount = response.data.meta.total_count || 0;
          
          // For client-side filtering, handle pagination differently based on filter type
          if (needsClientSideFiltering) {
            if (organizationFilter === 'other-orgs' || organizationFilter === 'other-schools' || organizationFilter === 'companies') {
              // For 'other-orgs', 'other-schools', and 'companies': Use client-side pagination
              // We've already filtered all projects and stored them in allFilteredProjects
              const filteredCount = allFilteredProjects.length;
              totalCount = filteredCount;
              totalPages = Math.max(1, Math.ceil(filteredCount / 12));
            }
          }
          
          setProjectTotalPages(totalPages);
          setProjectTotalCount(totalCount);
        }
      }
      setInitialLoad(false);
    } catch (err) {
      console.error('Erreur lors de la récupération des projets:', err);
      showError('Erreur lors de la récupération des projets.');
      setProjects([]);
      setProjectTotalPages(1);
      setProjectTotalCount(0);
      setInitialLoad(false);
    } finally {
      setIsLoadingMainProjects(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showingPageType, state.user, fetchPublicProjects, fetchMyProjects, fetchOrganizationProjects, isMinorPersonalUser, isPersonalUser, isTeacher, organizationFilter, myProjectsPage, projectPage, debouncedSearchTerm, statusFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isProjectDropdownOpen && !target.closest('.dropdown')) {
        setIsProjectDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProjectDropdownOpen]);

  // Open create modal from URL (e.g. from Sidebar "Actions rapides" -> Créer un projet)
  useEffect(() => {
    const open = searchParams.get('open');
    const variant = searchParams.get('variant');
    if (open !== 'create') return;
    setSelectedProject(null);
    setDuplicateSourceProject(null);
    if (variant === 'mlds' || variant === 'mlds-remediation') {
      const nextVariant: 'perseverance' | 'remediation' =
        variant === 'mlds-remediation' ? 'remediation' : 'perseverance';
      setMldsProjectVariant(nextVariant);
      if (state.showingPageType === 'user') {
        setIsSubscriptionModalOpen(true);
      } else {
        setIsMLDSProjectModalOpen(true);
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('open');
        next.delete('variant');
        return next;
      }, { replace: true });
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('open');
      next.delete('variant');
      return next;
    }, { replace: true });
    navigate('/create?type=project');
    setCurrentPage('create');
  }, [searchParams, state.showingPageType, setSearchParams]);

  // --- Fetch des projets au chargement ---
  useEffect(() => {
    // Reset pagination and loading state when dashboard type changes
    setProjectPage(1);
    setMyProjectsPage(1);
    setMldsProjectsPage(1);
    setMldsRemediationProjectsPage(1);
    setInitialLoad(true);
    setIsLoadingMainProjects(true);
    setDraftProjects([]);
    filtersEffectInitialMount.current = true;
    initialProjectsFetched.current = false;
    invalidateMldsCache();
    if (isTeacher) {
      void (async () => {
        await fetchMyProjects(1);
        void refreshMldsProjects(true);
      })();
      fetchDraftProjects();
    } else if (state.showingPageType === 'user') {
      void (async () => {
        if (isMinorPersonalUser) {
          await fetchOrganizationProjects(1);
        } else {
          await fetchPublicProjects(1);
        }
        await fetchMyProjects(1);
        void refreshMldsProjects(true);
      })();
    } else {
      // Pro / Edu: main list first, then MLDS prefetch in background
      void (async () => {
        await fetchProjects(1);
        initialProjectsFetched.current = true;
        void refreshMldsProjects(true);
      })();
      fetchDraftProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showingPageType, isMinorPersonalUser]); // isMinorPersonalUser: under-15 users get org projects on Nouveautés

  // Fetch projects when projectPage changes (for pro/edu dashboards and user "Nouveautés" tab)
  useEffect(() => {
    // For client-side paginated filters, use stored filtered projects
    if ((organizationFilter === 'other-orgs' || organizationFilter === 'other-schools' || organizationFilter === 'companies') && allFilteredProjects.length > 0) {
      const perPage = 12;
      const startIndex = (projectPage - 1) * perPage;
      const endIndex = startIndex + perPage;
      const paginatedProjects = allFilteredProjects.slice(startIndex, endIndex);
      
      // Map to Project format
      const formattedProjects: Project[] = paginatedProjects.map((p: any) => {
        return mapApiProjectToFrontendProject(p, state.showingPageType, state.user);
      });
      
      // Store raw API projects for permission checks
      const newRawProjectsMap = new Map<string, any>();
      paginatedProjects.forEach((p: any) => {
        if (p.id) {
          newRawProjectsMap.set(p.id.toString(), p);
        }
      });
      setRawProjectsMap(prev => {
        const merged = new Map(prev);
        newRawProjectsMap.forEach((value, key) => merged.set(key, value));
        return merged;
      });
      
      // Badge counts are now included in API response via badge_count
      setProjects(formattedProjects);
      
      // Update pagination metadata
      const totalCount = allFilteredProjects.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / 12));
      setProjectTotalPages(totalPages);
      setProjectTotalCount(totalCount);
      
      return; // Don't fetch from API
    }
    
    // For other cases, fetch from API
    if (isTeacher) {
      // Teachers only see their own projects
      fetchMyProjects(myProjectsPage);
    } else if (state.showingPageType === 'user' && activeTab === 'nouveautes') {
      if (isMinorPersonalUser) {
        fetchOrganizationProjects(projectPage);
      } else {
        fetchPublicProjects(projectPage);
      }
    } else if (!isPersonalUser) {
      // Pour les rôles pro et edu, recharger les projets avec la page
      // Éviter le doublon au premier rendu : skip si on est sur page 1 avec filtre 'my-org' et que le fetch initial n'a pas encore été fait
      // (le fetch initial se charge du premier appel)
      const isInitialLoad = projectPage === 1 && organizationFilter === 'my-org' && !initialProjectsFetched.current;
      if (!isInitialLoad) {
        fetchProjects(projectPage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectPage, organizationFilter, allFilteredProjects, isMinorPersonalUser]);

  useEffect(() => {
    if (state.showingPageType !== 'user') return;
    if (activeTab === 'nouveautes') {
      setProjectPage(1);
      if (isMinorPersonalUser) {
        fetchOrganizationProjects(1);
      } else {
        fetchPublicProjects(1);
      }
      return;
    }
    if (activeTab === 'mes-projets') {
      setMyProjectsPage(1);
      fetchMyProjects(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (isPersonalUser) return;
    setProjectPage(1);
    fetchProjects(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, organizationFilter, state.showingPageType]);

  // Fetch my projects when myProjectsPage changes (for user/teacher "Mes projets" tab)
  useEffect(() => {
    if ((state.showingPageType === 'user' && activeTab === 'mes-projets') || isTeacher) {
      fetchMyProjects(myProjectsPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myProjectsPage]);

  // MLDS pagination: slice from cached filtered dataset (no API refetch)
  useEffect(() => {
    if (activeTab === 'mlds-projects' && mldsProjectsCacheRef.current) {
      applyMldsDisplay(mldsProjectsPage, 'perseverance');
    }
  }, [mldsProjectsPage, activeTab, applyMldsDisplay]);

  useEffect(() => {
    if (activeTab === 'mlds-remediation-projects' && mldsProjectsCacheRef.current) {
      applyMldsDisplay(mldsRemediationProjectsPage, 'remediation');
    }
  }, [mldsRemediationProjectsPage, activeTab, applyMldsDisplay]);

  // MLDS search and filters (except organisation porteuse): re-apply on full cache
  useEffect(() => {
    if (activeTab !== 'mlds-projects' && activeTab !== 'mlds-remediation-projects') return;
    if (!mldsProjectsCacheRef.current) return;

    setMldsProjectsPage(1);
    setMldsRemediationProjectsPage(1);
    applyMldsDisplay(1, 'perseverance');
    applyMldsDisplay(1, 'remediation');
  }, [
    activeTab,
    debouncedSearchTerm,
    applyMldsDisplay,
    mldsPerseveranceRequestedByFilter,
    mldsPerseveranceTargetAudienceFilter,
    mldsPerseveranceActionObjectivesFilter,
    mldsPerseveranceStatusFilter,
    mldsPerseveranceStartDate,
    mldsPerseveranceEndDate,
    mldsRemediationRequestedByFilter,
    mldsRemediationTargetAudienceFilter,
    mldsRemediationActionObjectivesFilter,
    mldsRemediationStatusFilter,
    mldsRemediationStartDate,
    mldsRemediationEndDate,
  ]);

  // Organisation porteuse (class): refetch to merge class-level MLDS API results
  useEffect(() => {
    if (activeTab !== 'mlds-projects' && activeTab !== 'mlds-remediation-projects') return;

    let cancelled = false;
    const run = async () => {
      setMldsProjectsPage(1);
      setMldsRemediationProjectsPage(1);
      await loadMldsProjectsRawAll(true);
      if (cancelled) return;
      applyMldsDisplay(1, 'perseverance');
      applyMldsDisplay(1, 'remediation');
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    activeTab,
    mldsPerseveranceOrganizationFilter,
    mldsRemediationOrganizationFilter,
    loadMldsProjectsRawAll,
    applyMldsDisplay,
  ]);

  // Reset filters when switching tabs
  useEffect(() => {
    // Reset MLDS filters when leaving MLDS tab
    if (activeTab !== 'mlds-projects' && activeTab !== 'mlds-remediation-projects') {
      setMldsPerseveranceRequestedByFilter('all');
      setMldsPerseveranceTargetAudienceFilter('all');
      setMldsPerseveranceActionObjectivesFilter('all');
      setMldsPerseveranceOrganizationFilter('all');
      setMldsPerseveranceStatusFilter('all');
      setMldsPerseveranceStartDate('');
      setMldsPerseveranceEndDate('');

      setMldsRemediationRequestedByFilter('all');
      setMldsRemediationTargetAudienceFilter('all');
      setMldsRemediationActionObjectivesFilter('all');
      setMldsRemediationOrganizationFilter('all');
      setMldsRemediationStatusFilter('all');
      setMldsRemediationStartDate('');
      setMldsRemediationEndDate('');

      // Also reset general filters to avoid "empty list" surprises after MLDS filtering
      setPathwayFilter('all');
      setVisibilityFilter('all');
    }
    // Reset regular filters when entering MLDS tab
    if (activeTab === 'mlds-projects' || activeTab === 'mlds-remediation-projects') {
      setPathwayFilter('all');
      setVisibilityFilter('all');
    }
  }, [activeTab]);

  // Switch away from MLDS tab when there are no MLDS projects at all (tab is hidden)
  useEffect(() => {
    if (mldsCatalogCounts.perseverance === 0 && activeTab === 'mlds-projects') {
      setActiveTab(isTeacher ? 'mes-projets' : 'nouveautes');
    }
  }, [mldsCatalogCounts.perseverance, activeTab, isTeacher]);

  useEffect(() => {
    if (mldsCatalogCounts.remediation === 0 && activeTab === 'mlds-remediation-projects') {
      setActiveTab(isTeacher ? 'mes-projets' : 'nouveautes');
    }
  }, [mldsCatalogCounts.remediation, activeTab, isTeacher]);

  // Charger les brouillons quand state.user est dispo pour pro/edu (contextId peut être résolu)
  // Corrige le cas où l'effet initial a tourné avant que user soit chargé → compteur à 0
  useEffect(() => {
    if (state.showingPageType !== 'pro' && state.showingPageType !== 'edu') return;
    if (!state.user?.id || !state.user?.available_contexts) return;
    if (draftProjects.length > 0) return;
    fetchDraftProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user?.id, state.showingPageType, state.user?.available_contexts]);

  // Recharger les brouillons à l'ouverture de l'onglet (données à jour)
  useEffect(() => {
    if (activeTab === 'brouillons' && (isTeacher || state.showingPageType === 'pro' || state.showingPageType === 'edu')) {
      fetchDraftProjects();
    }
  }, [activeTab, isTeacher, state.showingPageType, fetchDraftProjects]);

  // Recharger les archives à l'ouverture de l'onglet (données à jour)
  useEffect(() => {
    if (activeTab === 'archives') {
      fetchArchivedProjects();
    }
  }, [activeTab, fetchArchivedProjects]);

  // Reset pagination and re-fetch when filters change
  useEffect(() => {
    // Reset to page 1 when filters change
    setProjectPage(1);
    setMyProjectsPage(1);
    setMldsProjectsPage(1);
    setMldsRemediationProjectsPage(1);

    // Éviter un doublon au montage : l'effet [projectPage, ...] fait déjà le premier fetch pour pro/edu
    if (filtersEffectInitialMount.current) {
      filtersEffectInitialMount.current = false;
      return;
    }

    if (isTeacher) {
      fetchMyProjects(1);
    } else if (state.showingPageType === 'user') {
      if (activeTab === 'nouveautes') {
        if (isMinorPersonalUser) {
          fetchOrganizationProjects(1);
        } else {
          fetchPublicProjects(1);
        }
      }
    } else if (!isPersonalUser) {
      fetchProjects(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    organizationFilter,
    statusFilter,
    pathwayFilter,
    searchTerm,
    startDate,
    endDate,
    visibilityFilter,

    isPersonalUser,
    isTeacher,
    activeTab
  ]);


  const handleCreateProject = () => {
    setSelectedProject(null);
    setDuplicateSourceProject(null);
    setIsProjectDropdownOpen(false);
    navigate('/create?type=project');
    setCurrentPage('create');
  };

  const handleCreateMLDSProject = () => {
    // Check if user is a personal user (teacher or user)
    const isPersonalUser = state.showingPageType === 'user';
    
    if (isPersonalUser) {
      // Show subscription required modal for personal users
      setIsSubscriptionModalOpen(true);
    } else {
      // Open MLDS project creation modal for organizational users
      setMldsProjectVariant('perseverance');
      setSelectedProject(null);
      setDuplicateSourceProject(null);
      setIsMLDSProjectModalOpen(true);
      setIsProjectDropdownOpen(false);
    }
  };

  const handleCreateMLDSRemediationProject = () => {
    const isPersonalUser = state.showingPageType === 'user';

    if (isPersonalUser) {
      setIsSubscriptionModalOpen(true);
    } else {
      setMldsProjectVariant('remediation');
      setSelectedProject(null);
      setDuplicateSourceProject(null);
      setIsMLDSProjectModalOpen(true);
      setIsProjectDropdownOpen(false);
    }
  };

  const handleEditProject = (project: Project) => {
    if (project.status === 'ended') {
      return;
    }
    const isMlds = (project as { mlds_information?: unknown }).mlds_information != null;
    if (isMlds) {
      setSelectedProject(project);
      setDuplicateSourceProject(null);
      setIsProjectModalOpen(true);
      return;
    }
    setSelectedProject(project);
    openProjectSpace(project.id, 'informations');
    setCurrentPage('project-space');
    navigate('/project-space');
  };

  const handleDuplicateProject = (project: Project) => {
    const raw = rawProjectsMap.get(project.id);
    const mldsInfo =
      (project as { mlds_information?: unknown }).mlds_information ?? raw?.mlds_information ?? null;
    const isMldsProject =
      mldsInfo != null ||
      project.pathway === 'mlds' ||
      (Array.isArray(project.pathways) && project.pathways.includes('mlds'));
    const typeMlds = (mldsInfo as { type_mlds?: string; type?: string } | null)?.type_mlds
      ?? (mldsInfo as { type_mlds?: string; type?: string } | null)?.type;
    const mldsVariant: 'perseverance' | 'remediation' =
      typeMlds === 'remediation' ? 'remediation' : 'perseverance';

    let sourceForDuplicate: Project = project;
    if (
      (project as { mlds_information?: unknown }).mlds_information == null &&
      raw?.mlds_information != null
    ) {
      sourceForDuplicate = { ...project, mlds_information: raw.mlds_information };
    }

    setSelectedProject(null);
    setDuplicateSourceProject(sourceForDuplicate);
    if (isMldsProject) {
      setMldsProjectVariant(mldsVariant);
      setIsMLDSProjectModalOpen(true);
    } else {
      setIsProjectModalOpen(true);
    }
  };

  const handleSaveProject = async (projectData: Omit<Project, 'id'>) => {
    if (selectedProject) {
      // Mettre à jour localement et via le contexte (si besoin de persistance API, ajouter l'appel ici)
      updateProject(selectedProject.id, projectData);
      setProjects(prev => prev.map(p => p.id === selectedProject.id ? { ...p, ...projectData } : p));
    } else {
      // Pour la création, on ne fait pas de mise à jour locale car on va recharger depuis l'API
      // Le projet sera créé via l'API dans ProjectModal
    }
    setIsProjectModalOpen(false);
    setSelectedProject(null);
    setDuplicateSourceProject(null);
    
    // Rafraîchir la liste des projets après création/modification
    if (isTeacher) {
      await fetchMyProjects();
      invalidateMldsCache();
      await refreshMldsProjects(true);
      await fetchDraftProjects();
      await fetchArchivedProjects();
    } else if (isPersonalUser) {
      if (activeTab === 'nouveautes') {
        await fetchPublicProjects();
      } else if (activeTab === 'mes-projets') {
        await fetchMyProjects();
      } else if (activeTab === 'mlds-projects' || activeTab === 'mlds-remediation-projects') {
        invalidateMldsCache();
        await refreshMldsProjects(true);
      }
      await fetchArchivedProjects();
    } else {
      await fetchProjects();
      invalidateMldsCache();
      await refreshMldsProjects(true);
      await fetchDraftProjects();
      await fetchArchivedProjects();
    }
  };

  const handleManageProject = (project: Project) => {
    setSelectedProject(project);
    const isMlds = (project as { mlds_information?: unknown }).mlds_information != null;
    if (isMlds) {
      setCurrentPage('project-management');
      return;
    }
    if (project.status === 'draft') {
      openProjectSpace(project.id, 'gestion');
      setCurrentPage('project-space');
      navigate('/project-space');
      return;
    }
    openProjectAffiche(project.id);
    setCurrentPage('project-affiche');
    navigate('/project-affiche');
  };

  const handleCloseProject = (project: Project) => {
    setProjectToClose(project);
    setIsCloseProjectConfirmOpen(true);
  };

  const confirmCloseProjectIntent = () => {
    if (!projectToClose) return;
    setIsCloseProjectConfirmOpen(false);

    const isMlds = projectToClose?.mlds_information != null;
    if (isMlds) {
      setIsCloseProjectModalOpen(true);
    } else {
      confirmCloseProject(undefined, projectToClose);
    }
  };

  const cancelCloseProject = () => {
    setIsCloseProjectConfirmOpen(false);
    setIsCloseProjectModalOpen(false);
    setProjectToClose(null);
  };

  const confirmCloseProject = async (bilanData?: BilanData, projectOverride?: Project) => {
    const proj = projectOverride ?? projectToClose;
    if (!proj) return;

    setIsClosingProject(true);
    try {
      const projectId = Number.parseInt(proj.id, 10);
      if (Number.isNaN(projectId)) {
        console.error('Invalid project ID:', proj.id);
        showError('Identifiant du projet invalide.');
        setIsClosingProject(false);
        return;
      }

      // MLDS closure path: POST /mlds_bilan performs closure server-side.
      if (bilanData) {
        const payload = buildMldsBilanPayload(bilanData, proj.mlds_information ?? null);
        await postMldsBilan(projectId, payload);
      } else {
        // Standard closure path: explicit close endpoint.
        await closeProject(projectId);
      }

      // Update local state
      setProjects(prev => prev.map(p => p.id === proj.id ? { ...p, status: 'ended' } : p));
      setMyProjects(prev => prev.map(p => p.id === proj.id ? { ...p, status: 'ended' } : p));
      setMldsProjects(prev => prev.map(p => p.id === proj.id ? { ...p, status: 'ended' } : p));

      setIsCloseProjectModalOpen(false);
      setProjectToClose(null);
      if (proj.mlds_information == null) {
        setBornProofProject({ ...proj, status: 'ended' });
        void fetchLifecycleCounts();
      }
    } catch (error: any) {
      console.error('Error closing project:', error);
      if (error?.response?.status === 403) {
        showError('Vous n’êtes pas autorisé à clôturer ce projet.');
      } else if (error?.response?.status === 422) {
        const details = error?.response?.data?.details;
        showError(Array.isArray(details) ? details.join(', ') : (error?.response?.data?.message || 'Clôture impossible.'));
      } else {
        showError('Erreur lors de la clôture du projet.');
      }
    } finally {
      setIsClosingProject(false);
    }
  };

  const handleDeleteProject = (project: Project) => {
    const rawApiProject = rawProjectsMap.get(project.id);
    console.log('[Projects] Supprimer — projet cliqué', {
      project,
      activeTab,
      rawApiProject,
      canDeleteOwner: rawApiProject
        ? canUserDeleteProject(rawApiProject, state.user?.id?.toString())
        : project.responsible?.id?.toString() === state.user?.id?.toString(),
    });

    if (project.status === 'ended') {
      console.warn('[Projects] Supprimer — bloqué (projet terminé)', project.id);
      return;
    }
    if (project.status !== 'draft' && project.status !== 'archived') {
      console.warn('[Projects] Supprimer — bloqué (statut non supprimable)', project.status);
      return;
    }

    setProjectToDelete(project);
    setIsDeleteModalOpen(true);
    console.log('[Projects] Supprimer — modale ouverte', { id: project.id, title: project.title });
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;
    
    try {
      await deleteProject(Number(projectToDelete.id));
      // Mise à jour de l'affichage local
      setProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
      setMyProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
      setMldsProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
      setArchivedProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
      setDraftProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
      setSelectedProject(null);

      // Rafraîchir en fonction de l'onglet actif
      if (activeTab === 'archives') {
        await fetchArchivedProjects();
      } else if (activeTab === 'mlds-projects' || activeTab === 'mlds-remediation-projects') {
        invalidateMldsCache();
        await refreshMldsProjects(true);
      } else if (activeTab === 'brouillons') {
        await fetchDraftProjects();
      } else if (isPersonalUser && activeTab === 'mes-projets') {
        await fetchMyProjects();
      }
      
      // Close modal and reset state
      setIsDeleteModalOpen(false);
      setProjectToDelete(null);
    } catch (err: any) {
      console.error('Erreur lors de la suppression du projet:', err);
      if (err?.response?.status === 403) {
        showError('Vous n’êtes pas autorisé à supprimer ce projet.');
      } else {
        showError('Erreur lors de la suppression du projet.');
      }
      setIsDeleteModalOpen(false);
      setProjectToDelete(null);
    }
  };

  const cancelDeleteProject = () => {
    setIsDeleteModalOpen(false);
    setProjectToDelete(null);
  };

  const handleExportProjects = () => {
    console.log('Export projects');
  };

  // Select projects to display based on active tab
  // Teachers only see their own projects (myProjects) or MLDS projects
  // Regular users see public projects, their own projects, or MLDS projects based on activeTab
  // Organization users (school/company) see their organization's projects or MLDS projects
  let projectsToDisplay: Project[];
  
  if (activeTab === 'mlds-projects') {
    projectsToDisplay = mldsProjects;
  } else if (activeTab === 'mlds-remediation-projects') {
    projectsToDisplay = mldsRemediationProjects;
  } else if (activeTab === 'brouillons') {
    // Teacher and pro/edu: use dedicated draft list (loaded when tab is active)
    if (isTeacher || state.showingPageType === 'pro' || state.showingPageType === 'edu') {
      projectsToDisplay = draftProjects;
    } else {
      // User (personal): draft projects from projects + myProjects
      const allProjects = [...projects, ...myProjects];
      projectsToDisplay = allProjects.filter(p => p.status === 'draft');
    }
  } else if (activeTab === 'archives') {
    projectsToDisplay = archivedProjects;
  } else if (isTeacher) {
    projectsToDisplay = myProjects;
  } else if (isPersonalUser) {
    projectsToDisplay = activeTab === 'nouveautes' ? projects : myProjects;
  } else {
    projectsToDisplay = projects;
  }

  // Load all classes (levels) for the MLDS "Organisations porteuses" filter
  useEffect(() => {
    const orgType = getOrganizationType(state.showingPageType);
    const orgId = getSelectedOrganizationId();

    // Only relevant for MLDS tabs (filter is not shown elsewhere)
    if (activeTab !== 'mlds-projects' && activeTab !== 'mlds-remediation-projects') {
      setMldsFilterClasses([]);
      return;
    }

    let cancelled = false;

    const fetchClasses = async () => {
      try {
        // Teacher: fetch managed classes from /teachers/classes
        if (state.showingPageType === 'teacher') {
          const res = await getTeacherClasses(1, 1000);
          const data = res.data?.data ?? res.data;
          const list = Array.isArray(data) ? data : [];
          if (!cancelled) setMldsFilterClasses(list);
          return;
        }

        // School (edu) context: fetch school levels
        if (orgType === 'school' && orgId) {
          const res = await getSchoolLevels(orgId, 1, 1000);
          const list = res.data?.data ?? res.data ?? [];
          if (!cancelled) setMldsFilterClasses(Array.isArray(list) ? list : []);
          return;
        }

        if (!cancelled) setMldsFilterClasses([]);
      } catch (e) {
        console.error('Error fetching classes for MLDS filter:', e);
        if (!cancelled) setMldsFilterClasses([]);
      }
    };

    fetchClasses();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, state.showingPageType, state.user]);

  const mldsClassOptions = React.useMemo(() => {
    return [...mldsFilterClasses]
      .filter((c) => c?.id != null)
      .map((c) => {
        const baseName = (c.name || '').trim();
        const levelSuffix = c.level ? ` - ${c.level}` : '';
        const label = `${baseName}${levelSuffix}`.trim();
        return { value: String(c.id), label: label || `Classe #${c.id}` };
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  }, [mldsFilterClasses]);

  // Calculate draft projects count (teacher/pro/edu: use draftProjects when loaded; else count from current lists)
  const draftProjectsCount = React.useMemo(() => {
    if (isTeacher || state.showingPageType === 'pro' || state.showingPageType === 'edu') {
      return draftProjects.length;
    }
    const allProjects = [...projects, ...myProjects];
    return allProjects.filter(p => p.status === 'draft').length;
  }, [isTeacher, state.showingPageType, draftProjects, projects, myProjects]);

  // Note: tab counters should rely on API `total_count` when paginated.

  // Calculate archived projects count from dedicated archived list
  const archivedProjectsCount = archivedProjects.length;

  const overdueOwnedProjectsCount = React.useMemo(() => {
    const currentUserId = state.user?.id?.toString();
    if (!currentUserId) return 0;

    const allKnownProjects = [...projects, ...myProjects, ...mldsProjects, ...mldsRemediationProjects];
    const uniqueById = new Map<string, Project>();
    allKnownProjects.forEach((project) => {
      if (project.id) uniqueById.set(project.id, project);
    });

    return Array.from(uniqueById.values()).filter((project) => {
      const isOwner = project.responsible?.id?.toString() === currentUserId;
      return isOwner && project.status === 'in_progress' && Boolean(project.showEndDateWarning);
    }).length;
  }, [state.user?.id, projects, myProjects, mldsProjects, mldsRemediationProjects]);


  // Filter projects based on search and filter criteria
  // Note: On filtre maintenant sur 'projectsToDisplay' (local state) et non 'state.projects'
  // For brouillons tab, only show draft projects
  // For Projets tab (nouveautes) and mes-projets, exclude draft projects (they have their own tab)
  // For archives tab, only show archived projects
  const isMldsPerseveranceTab = activeTab === 'mlds-projects';
  const isMldsRemediationTab = activeTab === 'mlds-remediation-projects';
  const isMldsTab = isMldsPerseveranceTab || isMldsRemediationTab;

  const mldsRequestedByFilter = isMldsRemediationTab ? mldsRemediationRequestedByFilter : mldsPerseveranceRequestedByFilter;
  const setMldsRequestedByFilter = isMldsRemediationTab ? setMldsRemediationRequestedByFilter : setMldsPerseveranceRequestedByFilter;
  const mldsTargetAudienceFilter = isMldsRemediationTab ? mldsRemediationTargetAudienceFilter : mldsPerseveranceTargetAudienceFilter;
  const setMldsTargetAudienceFilter = isMldsRemediationTab ? setMldsRemediationTargetAudienceFilter : setMldsPerseveranceTargetAudienceFilter;
  const mldsActionObjectivesFilter = isMldsRemediationTab ? mldsRemediationActionObjectivesFilter : mldsPerseveranceActionObjectivesFilter;
  const setMldsActionObjectivesFilter = isMldsRemediationTab ? setMldsRemediationActionObjectivesFilter : setMldsPerseveranceActionObjectivesFilter;
  const mldsOrganizationFilter = isMldsRemediationTab ? mldsRemediationOrganizationFilter : mldsPerseveranceOrganizationFilter;
  const setMldsOrganizationFilter = isMldsRemediationTab ? setMldsRemediationOrganizationFilter : setMldsPerseveranceOrganizationFilter;

  const mldsStatusFilter = isMldsRemediationTab ? mldsRemediationStatusFilter : mldsPerseveranceStatusFilter;
  const setMldsStatusFilter = isMldsRemediationTab ? setMldsRemediationStatusFilter : setMldsPerseveranceStatusFilter;
  const mldsStartDate = isMldsRemediationTab ? mldsRemediationStartDate : mldsPerseveranceStartDate;
  const setMldsStartDate = isMldsRemediationTab ? setMldsRemediationStartDate : setMldsPerseveranceStartDate;
  const mldsEndDate = isMldsRemediationTab ? mldsRemediationEndDate : mldsPerseveranceEndDate;
  const setMldsEndDate = isMldsRemediationTab ? setMldsRemediationEndDate : setMldsPerseveranceEndDate;

  const disableLocalSearchForUserDashboard =
    state.showingPageType === 'user' && (activeTab === 'nouveautes' || activeTab === 'mes-projets');
  const disableLocalSearchForOrgMyOrg =
    !isPersonalUser && organizationFilter === 'my-org' && (activeTab === 'nouveautes' || isLifecycleTab);
  const normalizedSearch = searchTerm.toLowerCase();
  const filteredProjects = projectsToDisplay.filter(project => {
    // MLDS tabs: filtering and pagination are handled in applyMldsDisplay
    if (isMldsTab) return true;

    // Archives tab: only archived, with type filter
    if (activeTab === 'archives') {
      if (project.status !== 'archived') return false;
      const isMLDS = Boolean((project as any).mlds_information);
      if (archivedTypeFilter === 'classic' && isMLDS) return false;
      if (archivedTypeFilter === 'mlds' && !isMLDS) return false;
    } else if (project.status === 'archived') {
      // Other tabs: never show archived
      return false;
    }
    // For brouillons tab, only show draft projects
    if (activeTab === 'brouillons' && project.status !== 'draft') {
      return false;
    }
    // For Projets tab (nouveautes) and Mes projets (mes-projets), exclude draft projects (they have their own tab)
    if ((activeTab === 'nouveautes' || activeTab === 'mes-projets' || isLifecycleTab) && project.status === 'draft') {
      return false;
    }
    // Search filter
    const matchesSearch = disableLocalSearchForUserDashboard || disableLocalSearchForOrgMyOrg || searchTerm === '' ||
      project.title.toLowerCase().includes(normalizedSearch) ||
      project.description.toLowerCase().includes(normalizedSearch) ||
      project.owner.toLowerCase().includes(normalizedSearch) ||
      (project.pathway && project.pathway.toLowerCase().includes(normalizedSearch)) ||
      project.status.toLowerCase().includes(normalizedSearch);

    // Pathway filter (skip for MLDS projects and brouillons)
    const matchesPathway = isMldsTab || activeTab === 'brouillons' || pathwayFilter === 'all' || project.pathway === pathwayFilter;

    // Status filter (skip for brouillons tab - all shown are drafts)
    const effectiveStatusFilter = isMldsTab ? mldsStatusFilter : statusFilter;
    const matchesStatus = activeTab === 'brouillons' || effectiveStatusFilter === 'all' ||
      project.status === effectiveStatusFilter ||
      (effectiveStatusFilter === 'draft' && project.status === 'draft') ||
      (effectiveStatusFilter === 'À venir' && project.status === 'coming') ||
      (effectiveStatusFilter === 'En cours' && project.status === 'in_progress') ||
      (effectiveStatusFilter === 'Terminée' && project.status === 'ended') ||
      (effectiveStatusFilter === 'À valider' && (project.status === 'pending_validation'));

    // Organization filter is now handled by API, no client-side filtering needed
    const matchesOrganization = true;

    // Visibility filter (skip for MLDS projects and brouillons)
    const matchesVisibility = isMldsTab || activeTab === 'brouillons' || visibilityFilter === 'all' ||
      (visibilityFilter === 'public' && (!project.visibility || project.visibility === 'public')) ||
      (visibilityFilter === 'private' && project.visibility === 'private');

    // Date filters
    let matchesStartDate = true;
    let matchesEndDate = true;
    
    const effectiveStartDate = isMldsTab ? mldsStartDate : startDate;
    const effectiveEndDate = isMldsTab ? mldsEndDate : endDate;

    if (effectiveStartDate && project.startDate) {
      matchesStartDate = new Date(project.startDate) >= new Date(effectiveStartDate);
    }
    
    if (effectiveEndDate && project.endDate) {
      matchesEndDate = new Date(project.endDate) <= new Date(effectiveEndDate);
    }

    // MLDS-specific filters (only apply if MLDS tab is active)
    let matchesMldsRequestedBy = true;
    let matchesMldsTargetAudience = true;
    let matchesMldsActionObjectives = true;
    let matchesMldsOrganization = true;
    
    if (isMldsTab && project.mlds_information) {
      // Filter by requested_by
      if (mldsRequestedByFilter !== 'all') {
        matchesMldsRequestedBy = project.mlds_information.requested_by === mldsRequestedByFilter;
      }
      
      // Filter by target_audience
      if (mldsTargetAudienceFilter !== 'all') {
        matchesMldsTargetAudience = project.mlds_information.target_audience === mldsTargetAudienceFilter;
      }
      
      // Filter by action_objectives (array contains the selected objective)
      if (mldsActionObjectivesFilter !== 'all') {
        matchesMldsActionObjectives = project.mlds_information.action_objectives && 
          Array.isArray(project.mlds_information.action_objectives) &&
          project.mlds_information.action_objectives.includes(mldsActionObjectivesFilter);
      }
      
      // Filter by school_level_ids (classes) for "Organisations porteuses"
      if (mldsOrganizationFilter !== 'all') {
        const selectedLevelId = Number.parseInt(mldsOrganizationFilter, 10);
        if (!Number.isNaN(selectedLevelId)) {
          matchesMldsOrganization =
            Array.isArray((project.mlds_information as any).school_level_ids) &&
            (project.mlds_information as any).school_level_ids.includes(selectedLevelId);
        } else {
          // Fallback for legacy/edge cases where filter value isn't a numeric id
          matchesMldsOrganization =
            project.mlds_information.organization_names &&
            Array.isArray(project.mlds_information.organization_names) &&
            project.mlds_information.organization_names.includes(mldsOrganizationFilter);
        }
      }
    }

    const matchesFinancement =
      isMldsTab ||
      activeTab === 'brouillons' ||
      financementFilter === 'all' ||
      (financementFilter === 'with' && Boolean(project.hasFunders)) ||
      (financementFilter === 'without' && !project.hasFunders);

    return matchesSearch && matchesPathway && matchesStatus && matchesOrganization && matchesVisibility && matchesStartDate && matchesEndDate && matchesMldsRequestedBy && matchesMldsTargetAudience && matchesMldsActionObjectives && matchesMldsOrganization && matchesFinancement;
  });

  const projectsHeading =
    state.showingPageType === 'user'
      ? 'Rechercher une idée de projet sur Kinship'
      : activeTab === 'je-finance'
        ? 'Tableau de bord financeur'
        : 'Gestion des projets';

  return (
    <section className="flex flex-col gap-12 p-8 with-sidebar">
      {state.showingPageType === 'user' && (
        <div className="dashboard-back-link-wrap">
          <button type="button" className="dashboard-back-link" onClick={() => { setCurrentPage('dashboard'); navigate('/dashboard'); }}>
            ← Vers mon tableau de bord
          </button>
        </div>
      )}
      {/* Section Title + Actions */}
      <div className="flex justify-between items-start">
        <div className="flex gap-2 items-center w-full section-title-left">
          <img src="/icons_logo/Icon=projet.svg" alt="Projets" className="section-icon" />
          <h2>{projectsHeading}</h2>
        </div>
        <div className="projects-actions">
          {state.showingPageType !== 'user' && !isMinorPersonalUser && (
            <div className="dropdown" style={{ position: 'relative' }}>
              <button className="btn btn-outline" disabled title="Disponible très bientôt" onClick={handleExportProjects}>
                <i className="fas fa-download"></i> Exporter
              </button>
            </div>
          )}
          {state.showingPageType !== 'user' && !isMinorPersonalUser && (
          <div className="dropdown" style={{ position: 'relative' }}>
            {state.showingPageType === 'pro' ? (
              <button
                className="btn btn-primary"
                onClick={handleCreateProject}
              >
                <i className="fas fa-plus"></i> Créer un projet
              </button>
            ) : (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                >
                  <i className="fas fa-plus"></i> Créer un projet
                  <i className={`fas fa-chevron-${isProjectDropdownOpen ? 'up' : 'down'}`} style={{ marginLeft: '8px' }}></i>
                </button>
                {isProjectDropdownOpen && (
                  <div
                    className="dropdown-menu"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      backgroundColor: 'white',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      borderRadius: '8px',
                      marginTop: '8px',
                      minWidth: '250px',
                      zIndex: 1000,
                      overflow: 'hidden'
                    }}
                  >
                    <button
                      onClick={handleCreateProject}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'white',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <i className="fas fa-folder" style={{ marginRight: '8px' }}></i>
                      Projet classique
                    </button>
                  <button
                    onClick={handleCreateMLDSProject}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'white',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <i className="fas fa-graduation-cap" style={{ marginRight: '8px' }}></i>
                    Projet MLDS Volet Persévérance Scolaire
                  </button>
                  <button
                    onClick={handleCreateMLDSRemediationProject}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'white',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                  >
                    <i className="fas fa-chalkboard-teacher" style={{ marginRight: '8px' }}></i>
                    Projet MLDS Volet Remédiation
                  </button>
                  </div>
                )}
              </>
            )}
          </div>
          )}
        </div>
      </div>

      {overdueOwnedProjectsCount > 0 && (
        <div
          style={{
            margin: '0 0 0.75rem 0',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            border: '1px solid #f59e0b',
            background: '#fffbeb',
            color: '#92400e',
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem'
          }}
        >
          <i className="fas fa-triangle-exclamation" />
          <span>
            Vous avez {overdueOwnedProjectsCount} projet{overdueOwnedProjectsCount > 1 ? 's' : ''} dont la date de fin est dépassée. Souhaitez-vous les clôturer ?
          </span>
        </div>
      )}

      {/* Tabs for all users */}
        <div className="filter-tabs" style={{ marginBottom: '24px' }}>
        {/* For users: show Nouveautés, Mes projets, Brouillons, MLDS, and Archives tabs */}
        {/* For users: show Nouveautés / Projets de mes établissements (minors), Mes projets, Brouillons, and MLDS tabs */}
        {state.showingPageType === 'user' && (
          <>
          <button 
            className={`filter-tab ${activeTab === 'nouveautes' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('nouveautes');
              setProjectPage(1); // Reset pagination when switching tabs
            }}
          >
              {isMinorPersonalUser
                ? `Projets de mes établissements/organisations (${projectTotalCount})`
                : `Nouveautés (${projectTotalCount})`}
          </button>
            <button 
              className={`filter-tab ${activeTab === 'mes-projets' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('mes-projets');
                setMyProjectsPage(1); // Reset pagination when switching tabs
              }}
            >
              Mes projets ({myProjectsTotalCount})
            </button>
            <button 
              className={`filter-tab ${activeTab === 'brouillons' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('brouillons');
              }}
            >
              Brouillons ({draftProjectsCount})
            </button>
            {mldsCatalogCounts.perseverance > 0 && (
              <button 
                className={`filter-tab ${activeTab === 'mlds-projects' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('mlds-projects');
                  setMldsProjectsPage(1); // Reset pagination when switching tabs
                }}
              >
                Projets MLDS Volet Persévérance ({mldsCatalogCounts.perseverance})
              </button>
            )}
            {mldsCatalogCounts.remediation > 0 && (
              <button
                className={`filter-tab ${activeTab === 'mlds-remediation-projects' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('mlds-remediation-projects');
                  setMldsRemediationProjectsPage(1);
                }}
              >
                Projets MLDS Volet Remédiation ({mldsCatalogCounts.remediation})
              </button>
            )}
            <button
              className={`filter-tab ${activeTab === 'archives' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('archives');
              }}
            >
              Archives ({archivedProjectsCount})
            </button>
          </>
        )}
        
        {/* For teachers: show Mes projets, Brouillons, MLDS, and Archives tabs */}
        {state.showingPageType === 'teacher' && (
          <>
            <button 
              className={`filter-tab ${activeTab === 'mes-projets' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('mes-projets');
                setMyProjectsPage(1); // Reset pagination when switching tabs
              }}
            >
              Mes projets ({myProjectsTotalCount})
            </button>
            <button 
              className={`filter-tab ${activeTab === 'brouillons' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('brouillons');
              }}
            >
              Brouillons ({draftProjectsCount})
            </button>
            {mldsCatalogCounts.perseverance > 0 && (
              <button 
                className={`filter-tab ${activeTab === 'mlds-projects' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('mlds-projects');
                  setMldsProjectsPage(1); // Reset pagination when switching tabs
                }}
              >
                Projets MLDS Volet Persévérance ({mldsCatalogCounts.perseverance})
              </button>
            )}
            {mldsCatalogCounts.remediation > 0 && (
              <button
                className={`filter-tab ${activeTab === 'mlds-remediation-projects' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('mlds-remediation-projects');
                  setMldsRemediationProjectsPage(1);
                }}
              >
                Projets MLDS Volet Remédiation ({mldsCatalogCounts.remediation})
              </button>
            )}
            <button
              className={`filter-tab ${activeTab === 'archives' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('archives');
              }}
            >
              Archives ({archivedProjectsCount})
            </button>
          </>
        )}
        
        {/* For pro/edu: show Projets, Brouillons, MLDS, and Archives tabs */}
        {(state.showingPageType === 'pro' || state.showingPageType === 'edu') && (
          <>
            <button
              className={`filter-tab ${activeTab === 'brouillons' ? 'active' : ''}`}
              onClick={() => selectHubTab('brouillons')}
            >
              Brouillons ({draftProjectsCount})
            </button>
            <button
              className={`filter-tab ${activeTab === 'coming' ? 'active' : ''}`}
              onClick={() => selectLifecycleTab('coming')}
            >
              À venir ({lifecycleCounts.coming})
            </button>
            <button
              className={`filter-tab ${activeTab === 'in_progress' ? 'active' : ''}`}
              onClick={() => selectLifecycleTab('in_progress')}
            >
              En cours ({lifecycleCounts.in_progress})
            </button>
            <button
              className={`filter-tab ${activeTab === 'ended' ? 'active' : ''}`}
              onClick={() => selectLifecycleTab('ended')}
            >
              Terminés ({lifecycleCounts.ended})
            </button>
            <button
              className={`filter-tab ${activeTab === 'archives' ? 'active' : ''}`}
              onClick={() => selectHubTab('archives')}
            >
              Archivés ({archivedProjectsCount})
            </button>
            {financedCount > 0 && (
              <button
                className={`filter-tab ${activeTab === 'je-finance' ? 'active' : ''}`}
                onClick={() => selectHubTab('je-finance')}
              >
                {jeFinanceLabel(financedCount)}
              </button>
            )}
            {mldsCatalogCounts.perseverance > 0 && (
              <button 
                className={`filter-tab ${activeTab === 'mlds-projects' ? 'active' : ''}`}
                onClick={() => {
                  selectHubTab('mlds-projects');
                  setMldsProjectsPage(1);
                }}
              >
                Projets MLDS Volet Persévérance ({mldsCatalogCounts.perseverance})
              </button>
            )}
            {mldsCatalogCounts.remediation > 0 && (
              <button
                className={`filter-tab ${activeTab === 'mlds-remediation-projects' ? 'active' : ''}`}
                onClick={() => {
                  selectHubTab('mlds-remediation-projects');
                  setMldsRemediationProjectsPage(1);
                }}
              >
                Projets MLDS Volet Remédiation ({mldsCatalogCounts.remediation})
              </button>
            )}
          </>
        )}
      </div>

      {activeTab !== 'je-finance' && (
      <div className="w-full projects-search-container">
        <div className="search-bar">
          <i className="fas fa-search search-icon"></i>
          <input
            type="text"
            className="w-full search-input"
            placeholder="Rechercher un projet par titre, mot clé, parcours…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filters-container">
          {/* Show MLDS-specific filters for MLDS tab, hide for brouillons */}
          {activeTab !== 'brouillons' && (
            (activeTab === 'mlds-projects' || activeTab === 'mlds-remediation-projects') ? (
              <>
                <div className="filter-group">
                  <label htmlFor="mlds-requested-by-filter">Demande faite par</label>
                  <select
                    id="mlds-requested-by-filter"
                    className="filter-select"
                    value={mldsRequestedByFilter}
                    onChange={(e) => setMldsRequestedByFilter(e.target.value)}
                  >
                    <option value="all">Tous</option>
                    <option value="departement">Département</option>
                    <option value="reseau_foquale">Réseau foquale</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label htmlFor="mlds-target-audience-filter">Public ciblé</label>
                  <select
                    id="mlds-target-audience-filter"
                    className="filter-select"
                    value={mldsTargetAudienceFilter}
                    onChange={(e) => setMldsTargetAudienceFilter(e.target.value)}
                  >
                    <option value="all">Tous</option>
                    {activeTab === 'mlds-remediation-projects' ? (
                      <>
                        <option value="mlds_assigned">Élèves affectés à la MLDS</option>
                        <option value="pafi_tdo">Élèves en PAFI TDO</option>
                      </>
                    ) : (
                      <>
                        <option value="students_without_solution">Élèves sans solution à la rentrée</option>
                        <option value="students_at_risk">Élèves en situation de décrochage</option>
                        <option value="school_teams">Équipes des établissements</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="filter-group">
                  <label htmlFor="mlds-action-objectives-filter">Objectifs de l&apos;action</label>
                  <select
                    id="mlds-action-objectives-filter"
                    className="filter-select"
                    value={mldsActionObjectivesFilter}
                    onChange={(e) => setMldsActionObjectivesFilter(e.target.value)}
                  >
                    <option value="all">Tous</option>
                    {activeTab === 'mlds-remediation-projects' ? (
                      <>
                        <option value="professional_discovery">Découverte des filières professionnelles</option>
                        <option value="student_mobility">Mobilité des élèves</option>
                        <option value="cps_development">Développement des CPS</option>
                        <option value="territory_partnership">Partenariats du territoire</option>
                        <option value="family_links">Liens avec les familles</option>
                        <option value="professional_development">Co-développement professionnel</option>
                        <option value="art_culture_path">Parcours d&apos;éducation artistique et culturel</option>
                        <option value="avenir_path">Parcours avenir</option>
                        <option value="citizen_path">Parcours citoyen</option>
                        <option value="physical_education">Éducation physique et sportive</option>
                        <option value="disciplinary_course">Cours disciplinaire</option>
                        <option value="job_discovery">Découverte des métiers</option>
                        <option value="training_discovery">Découverte des formations</option>
                        <option value="other">Autre</option>
                      </>
                    ) : (
                      <>
                        <option value="path_security">Sécurisation des parcours</option>
                        <option value="professional_discovery">Découverte professionnelle</option>
                        <option value="student_mobility">Mobilité des élèves</option>
                        <option value="cps_development">Développement des CPS</option>
                        <option value="territory_partnership">Partenariats du territoire</option>
                        <option value="family_links">Liens avec les familles</option>
                        <option value="professional_development">Développement professionnel</option>
                        <option value="other">Autre</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="filter-group">
                  <label htmlFor="mlds-organization-filter">Organisations porteuses</label>
                  <select
                    id="mlds-organization-filter"
                    className="filter-select"
                    value={mldsOrganizationFilter}
                    onChange={(e) => setMldsOrganizationFilter(e.target.value)}
                  >
                    <option value="all">Toutes</option>
                    {mldsClassOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label htmlFor="status-filter">Statut</label>
                  <select
                    id="status-filter"
                    className="filter-select"
                    value={mldsStatusFilter}
                    onChange={(e) => setMldsStatusFilter(e.target.value)}
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="draft">Brouillon</option>
                    <option value="to_process">À traiter</option>
                    <option value="pending_validation">À valider</option>
                    <option value="À venir">À venir</option>
                    <option value="En cours">En cours</option>
                    <option value="Terminée">TERMINÉ</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label htmlFor="start-date-filter">Date de début</label>
                  <input
                    id="start-date-filter"
                    type="date"
                    className="filter-select"
                    value={mldsStartDate}
                    onChange={(e) => setMldsStartDate(e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label htmlFor="end-date-filter">Date de fin</label>
                  <input
                    id="end-date-filter"
                    type="date"
                    className="filter-select"
                    value={mldsEndDate}
                    onChange={(e) => setMldsEndDate(e.target.value)}
                  />
                </div>
              </>
            ) : activeTab === 'archives' ? (
              <>
                <div className="filter-group">
                  <label htmlFor="archived-type-filter">Type de projet</label>
                  <select
                    id="archived-type-filter"
                    className="filter-select"
                    value={archivedTypeFilter}
                    onChange={(e) => setArchivedTypeFilter(e.target.value as 'all' | 'classic' | 'mlds')}
                  >
                    <option value="all">Tous les types</option>
                    <option value="classic">Classique</option>
                    <option value="mlds">MLDS</option>
                  </select>
                </div>
              </>
            ) : (
              <>
                {/* Organization filter only for pro and edu dashboards */}
                {(state.showingPageType === 'pro' || state.showingPageType === 'edu') && (
                <div className="filter-group">
                  <label htmlFor="organization-filter">Par Organisation</label>
                  <select
                    id="organization-filter"
                    className="filter-select"
                    value={organizationFilter}
                    onChange={(e) => setOrganizationFilter(e.target.value as 'my-org' | 'all-public' | 'school' | 'other-orgs' | 'other-schools' | 'companies')}
                  >
                    {state.showingPageType === 'pro' ? (
                      <>
                        <option value="my-org">Mon organisation</option>
                        <option value="all-public">Tous les projets</option>
                        <option value="school">Établissement</option>
                        <option value="other-orgs">Autres organisations</option>
                      </>
                    ) : (
                      <>
                        <option value="my-org">Mon établissement</option>
                        <option value="all-public">Tous les projets</option>
                        <option value="other-schools">Autres établissements</option>
                        <option value="companies">Organisations</option>
                      </>
                    )}
                  </select>
                </div>
              )}
              <div className="filter-group">
                <label htmlFor="pathway-filter">Parcours</label>
                <select
                  id="pathway-filter"
                  className="filter-select"
                  value={pathwayFilter}
                  onChange={(e) => setPathwayFilter(e.target.value)}
                >
                  <option value="all">Tous les parcours</option>
                  <option value="citoyen">Citoyen</option>
                  <option value="creativite">Créativité</option>
                  <option value="fabrication">Fabrication</option>
                  <option value="psychologie">Psychologie</option>
                  <option value="innovation">Innovation</option>
                  <option value="education">Éducation</option>
                  <option value="technologie">Technologie</option>
                  <option value="sante">Santé</option>
                  <option value="environnement">Environnement</option>
                  <option value="mlds">MLDS</option>
                  <option value="faj_co">FAJ Co</option>
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="financement-filter">Financement</label>
                <select
                  id="financement-filter"
                  className="filter-select"
                  value={financementFilter}
                  onChange={(e) => setFinancementFilter(e.target.value as 'all' | 'with' | 'without')}
                >
                  <option value="all">Tous</option>
                  <option value="with">Avec financeur</option>
                  <option value="without">Sans financeur</option>
                </select>
              </div>
              {!isOrgHub && (
              <div className="filter-group">
                <label htmlFor="status-filter">Statut</label>
                <select
                  id="status-filter"
                  className="filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Tous les statuts</option>
                  <option value="À venir">À venir</option>
                  <option value="En cours">En cours</option>
                  <option value="Terminée">TERMINÉ</option>
                </select>
              </div>
              )}
              <div className="filter-group">
                <label htmlFor="start-date-filter">Date de début</label>
                <input
                  id="start-date-filter"
                  type="date"
                  className="filter-select"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label htmlFor="end-date-filter">Date de fin</label>
                <input
                  id="end-date-filter"
                  type="date"
                  className="filter-select"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label htmlFor="visibility-filter">Visibilité</label>
                <select
                  id="visibility-filter"
                  className="filter-select"
                  value={visibilityFilter}
                  onChange={(e) => setVisibilityFilter(e.target.value)}
                >
                  <option value="all">Tous les projets</option>
                  <option value="public">Publics</option>
                  <option value="private">Privés</option>
                </select>
              </div>
              </>
            )
          )}
        </div>
      </div>
      )}

      {(activeTab === 'je-finance') ? (
        <FundedProjectsPage embedded />
      ) : (isLoadingMainProjects && !isMldsTab && (initialLoad || activeTab === 'nouveautes' || activeTab === 'mes-projets' || isLifecycleTab)) ||
        (isMldsTab && isLoadingMldsProjects && !mldsProjectsCacheRef.current) ||
        (activeTab === 'brouillons' && isLoadingDraftProjects && (isTeacher || state.showingPageType === 'pro' || state.showingPageType === 'edu')) ||
        (activeTab === 'archives' && isLoadingArchivedProjects) ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">{activeTab === 'brouillons' ? 'Chargement des brouillons...' : activeTab === 'archives' ? 'Chargement des archives...' : 'Chargement des projets...'}</p>
        </div>
      ) : filteredProjects.length > 0 ? (
        <>
          <div style={{ position: 'relative' }}>
            {isLoadingMainProjects && !initialLoad && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(255,255,255,0.65)',
                  zIndex: 5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '12px',
                  backdropFilter: 'blur(1px)'
                }}
              >
                <div className="loading-container" style={{ margin: 0, padding: '1rem' }}>
                  <div className="loading-spinner"></div>
                  <p className="loading-text">Chargement...</p>
                </div>
              </div>
            )}
            <div className="projects-grid" style={isLoadingMainProjects && !initialLoad ? { pointerEvents: 'none' } : undefined}>
            {filteredProjects.map((project, index) => {
              const isPersonalUser = state.showingPageType === 'teacher' || state.showingPageType === 'user';
              
              // Get raw API project data for permission checks
              const rawApiProject = rawProjectsMap.get(project.id);
              
              // Calculate permissions
              const userIdStr = state.user?.id?.toString();
              const canManage = rawApiProject
                ? canUserManageProject(rawApiProject, state.user)
                : Boolean(userIdStr && project.responsible?.id?.toString() === userIdStr);
              const isOwner = rawApiProject
                ? isUserProjectOwner(rawApiProject, userIdStr)
                : Boolean(userIdStr && project.responsible?.id?.toString() === userIdStr);
              const canDelete = rawApiProject ? canUserDeleteProject(rawApiProject, userIdStr) : isOwner;
              // Check co-owner status: first try rawApiProject, then fallback to project.coResponsibles
              let isCoOwner = false;
              
              if (rawApiProject && userIdStr) {
                isCoOwner = isUserProjectCoOwner(rawApiProject, userIdStr);
              }
              
              // Fallback: also check if user is in project.coResponsibles array (mapped from API)
              // This is important for teachers where rawApiProject might not always be available
              if (!isCoOwner && userIdStr) {
                // Check coResponsibles array
                if (project.coResponsibles && Array.isArray(project.coResponsibles) && project.coResponsibles.length > 0) {
                  isCoOwner = project.coResponsibles.some((co: any) => {
                    // Support both co.id (string) and co.id (number) comparisons
                    const coId = co?.id?.toString();
                    return coId && coId === userIdStr;
                  });
                }
                // Also check rawApiProject.project_members if available (for teachers)
                if (!isCoOwner && rawApiProject?.project_members && Array.isArray(rawApiProject.project_members)) {
                  isCoOwner = rawApiProject.project_members.some((member: any) => {
                    const memberUserId = member.user?.id?.toString() || member.id?.toString();
                    return memberUserId === userIdStr && member.role === 'co_owner' && member.status === 'confirmed';
                  });
                }
              }
              
              const isOwnerOrCoOwner = isOwner || isCoOwner;
              
              // Check if project is ended - disable edit/delete actions if true, but allow viewing
              const isProjectEnded = project.status === 'ended';
              const canClose = project.status === 'in_progress' && isOwner;
              const canDeleteProject =
                canDelete &&
                !isProjectEnded &&
                (project.status === 'draft' || project.status === 'archived');
              const canDuplicateProject =
                isProjectEnded &&
                isOwner &&
                state.showingPageType !== 'user';
              
              return (
                <ProjectCard
                  key={`${project.id ?? 'no-id'}-${project.title ?? 'untitled'}-${index}`}
                  project={project}
                  onEdit={!isProjectEnded ? () => handleEditProject(project) : undefined}
                  onManage={() => handleManageProject(project)} // Always allow viewing/managing
                  onDelete={canDeleteProject ? handleDeleteProject : undefined}
                  onClose={canClose ? () => handleCloseProject(project) : undefined}
                  isPersonalUser={isPersonalUser}
                  canManage={canManage && !isProjectEnded} // Can't manage if ended, but can view
                  canDelete={canDeleteProject}
                  onDuplicate={ () => handleDuplicateProject(project) }
                  canDuplicate={canDuplicateProject}
                  isOwnerOrCoOwner={isOwnerOrCoOwner}
                />
              );
            })}
            </div>
          </div>
          
          {/* Pagination Controls */}
          {(() => {
            // Determine which pagination state to use based on active tab
            let currentPage: number;
            let totalPages: number;
            let totalCount: number;
            let setCurrentPage: (page: number) => void;
            
            if (activeTab === 'mlds-projects') {
              currentPage = mldsProjectsPage;
              totalPages = mldsProjectsTotalPages;
              totalCount = mldsProjectsTotalCount;
              setCurrentPage = setMldsProjectsPage;
            } else if (activeTab === 'mlds-remediation-projects') {
              currentPage = mldsRemediationProjectsPage;
              totalPages = mldsRemediationProjectsTotalPages;
              totalCount = mldsRemediationProjectsTotalCount;
              setCurrentPage = setMldsRemediationProjectsPage;
            } else if (activeTab === 'mes-projets') {
              currentPage = myProjectsPage;
              totalPages = myProjectsTotalPages;
              totalCount = myProjectsTotalCount;
              setCurrentPage = setMyProjectsPage;
            } else if (activeTab === 'brouillons') {
              // No pagination for brouillons - show all draft projects
              return null;
            } else if (activeTab === 'archives') {
              // Archives are loaded as a full list (no pagination state here)
              return null;
            } else {
              currentPage = projectPage;
              totalPages = projectTotalPages;
              totalCount = projectTotalCount;
              setCurrentPage = setProjectPage;
            }
            
            if (totalPages <= 1) return null;
            
            return (
              <div className="pagination-container">
                <div className="pagination-info">
                  Page {currentPage} sur {totalPages} ({totalCount} projet{totalCount > 1 ? 's' : ''})
                </div>
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => {
                      const newPage = currentPage - 1;
                      setCurrentPage(newPage);
                    }}
                    disabled={isLoadingMainProjects || currentPage === 1}
                  >
                    <i className="fas fa-chevron-left"></i> Précédent
                  </button>
                  <div className="pagination-pages">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                          disabled={isLoadingMainProjects}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    className="pagination-btn"
                    onClick={() => {
                      const newPage = currentPage + 1;
                      setCurrentPage(newPage);
                    }}
                    disabled={isLoadingMainProjects || currentPage === totalPages}
                  >
                    Suivant <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            );
          })()}
        </>
      ) : !isLoadingMainProjects && (!isMldsTab || !isLoadingMldsProjects) ? (
        <div className="no-projects-message">
          <div className="no-projects-content">
            <i className="fas fa-folder-open" style={{ fontSize: '3rem', color: '#9ca3af', marginBottom: '1rem' }}></i>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
              Aucun projet trouvé
            </h3>
            <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
              {searchTerm || pathwayFilter !== 'all' || statusFilter !== 'all' || organizationFilter !== 'my-org' || visibilityFilter !== 'all' || startDate || endDate
                ? 'Aucun projet ne correspond à vos critères de recherche. Essayez de modifier vos filtres.'
                : 'Il n\'y a pas encore de projets disponibles.'}
            </p>
          </div>
        </div>
      ) : null}

      {isProjectModalOpen && (
        <ProjectModal
          project={selectedProject}
          duplicateFromProject={duplicateSourceProject}
          onClose={() => {
            setIsProjectModalOpen(false);
            setSelectedProject(null);
            setDuplicateSourceProject(null);
          }}
          onSave={handleSaveProject}
        />
      )}

      {isMLDSProjectModalOpen && (
        <MLDSProjectModal
          initialDataFromProject={duplicateSourceProject}
          variant={mldsProjectVariant}
          onClose={() => {
            setIsMLDSProjectModalOpen(false);
            setSelectedProject(null);
            setDuplicateSourceProject(null);
          }}
          onSave={handleSaveProject}
        />
      )}

      {isSubscriptionModalOpen && (
        <SubscriptionRequiredModal
          onClose={() => setIsSubscriptionModalOpen(false)}
          featureName="La création de projets"
        />
      )}

      {isDeleteModalOpen && projectToDelete && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Confirmer la suppression"
          message={`Êtes-vous sûr de vouloir supprimer ce projet ?\n\nCette action est irréversible. Le projet « ${projectToDelete.title} » sera définitivement supprimé.`}
          confirmText="Supprimer"
          cancelText="Annuler"
          variant="danger"
          onConfirm={confirmDeleteProject}
          onCancel={cancelDeleteProject}
        />
      )}

      <CloseProjectModal
        isOpen={isCloseProjectConfirmOpen && !!projectToClose}
        projectTitle={projectToClose?.title || ''}
        hasFunders={Boolean(projectToClose?.hasFunders)}
        isSubmitting={isClosingProject}
        onConfirm={confirmCloseProjectIntent}
        onCancel={cancelCloseProject}
      />

      {/* Modal bilan à la clôture du projet (projets MLDS uniquement) */}
      {isCloseProjectModalOpen && projectToClose && projectToClose.mlds_information != null && (
        <CloseProjectBilanModal
          key={projectToClose.id}
          projectTitle={projectToClose.title}
          mldsInfo={projectToClose.mlds_information ?? null}
          onClose={cancelCloseProject}
          onConfirm={(bilanData) => confirmCloseProject(bilanData)}
          isSubmitting={isClosingProject}
        />
      )}

      {bornProofProject && (
        <CloseProjectBirthOverlay
          title={bornProofProject.title}
          organization={bornProofProject.organization}
          onOpen={() => {
            const project = bornProofProject;
            setBornProofProject(null);
            setCurrentPage('pik');
            navigate(`/pik/preuve/pp/${project.id}`);
          }}
          onContinue={() => {
            const project = bornProofProject;
            setBornProofProject(null);
            handleManageProject(project);
          }}
        />
      )}

    </section>
  );
};

export default Projects;